/* ────────────────────────────────────────────────────────────
   Swipetronics — Custom flat-fintech illustrations (v3)
   Composition principle: ONE clear hero element + 2–3 floating
   cards that overlap and orbit it. Tighter framing, bigger pieces,
   more visible depth via layered shadows.
   Strict brand palette only.
   ──────────────────────────────────────────────────────────── */

function SharedDefs({ id }) {
  return (
    <defs>
      <pattern id={`${id}-dots`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
        <circle cx="1.6" cy="1.6" r="1.6" fill="#0072FF" fillOpacity="0.18" />
      </pattern>
      <linearGradient id={`${id}-panel`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#EAF3FF" />
        <stop offset="100%" stopColor="#F4F8FF" />
      </linearGradient>
      <linearGradient id={`${id}-card`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="100%" stopColor="#F4F8FF" />
      </linearGradient>
      <linearGradient id={`${id}-accent`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#0072FF" />
        <stop offset="100%" stopColor="#3A8EFF" />
      </linearGradient>
      <linearGradient id={`${id}-navy`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#0A2552" />
        <stop offset="100%" stopColor="#061A3A" />
      </linearGradient>
      <radialGradient id={`${id}-halo`} cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#0072FF" stopOpacity="0.25" />
        <stop offset="60%" stopColor="#0072FF" stopOpacity="0.06" />
        <stop offset="100%" stopColor="#0072FF" stopOpacity="0" />
      </radialGradient>
      <filter id={`${id}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="8" />
        <feOffset dx="0" dy="14" result="off" />
        <feComponentTransfer>
          <feFuncA type="linear" slope="0.22" />
        </feComponentTransfer>
        <feMerge>
          <feMergeNode />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <filter id={`${id}-soft`} x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="4" />
        <feOffset dx="0" dy="6" result="off" />
        <feComponentTransfer>
          <feFuncA type="linear" slope="0.18" />
        </feComponentTransfer>
        <feMerge>
          <feMergeNode />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}

/* Background panel + dotted texture + halo + 3 decorative geometric accents */
function PanelBackdrop({ id, accents, transparent = false }) {
  if (transparent) {
    // No card backdrop — illustration floats directly on parent surface.
    // Keep only the soft radial halo and decorative accents for atmospheric depth.
    return (
      <g>
        <circle cx="300" cy="300" r="240" fill={`url(#${id}-halo)`} />
        {accents}
      </g>
    );
  }
  return (
    <g>
      <rect x="0" y="0" width="600" height="600" rx="36" fill={`url(#${id}-panel)`} />
      <rect x="0" y="0" width="600" height="600" rx="36" fill={`url(#${id}-dots)`} />
      <circle cx="300" cy="300" r="220" fill={`url(#${id}-halo)`} />
      {/* decorative floating squares + rings */}
      {accents}
    </g>
  );
}

/* ════════════════════════════════════════════════════════════
   1) GROWTH — Tailored solutions for business growth
   Hero: large tilted dashboard window. Orbit: KPI card,
   tailored-plan card, stacked payment cards.
   ════════════════════════════════════════════════════════════ */
export function GrowthIllustration({ className = "" }) {
  const id = "g3";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Tailored dashboard showing revenue growth and customized payment plans"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        accents={
          <g>
            <circle cx="78"  cy="106" r="46" fill="#0072FF" fillOpacity="0.07" />
            <circle cx="520" cy="510" r="58" fill="#061A3A" fillOpacity="0.06" />
            <rect x="490" y="80" width="78" height="78" rx="20" fill="#0072FF" fillOpacity="0.05" transform="rotate(12 529 119)" />
            <rect x="60"  y="450" width="64" height="64" rx="18" fill="#0072FF" fillOpacity="0.07" transform="rotate(-8 92 482)" />
          </g>
        }
      />

      {/* HERO — tilted dashboard window with rich content */}
      <g transform="translate(86 132) rotate(-2 217 159)" filter={`url(#${id}-shadow)`}>
        <rect x="0" y="0" width="434" height="318" rx="22" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* top bar */}
        <rect x="0" y="0" width="434" height="44" rx="22" fill={`url(#${id}-navy)`} />
        <rect x="0" y="20" width="434" height="24" fill="#061A3A" />
        <circle cx="22" cy="22" r="5" fill="#0072FF" />
        <circle cx="42" cy="22" r="5" fill="#3A8EFF" />
        <circle cx="62" cy="22" r="5" fill="#FFFFFF" fillOpacity="0.55" />
        <rect x="160" y="14" width="160" height="16" rx="8" fill="#FFFFFF" fillOpacity="0.10" />
        <rect x="172" y="18" width="80" height="8" rx="4" fill="#FFFFFF" fillOpacity="0.6" />

        {/* sidebar */}
        <rect x="0" y="44" width="58" height="274" fill="#F4F8FF" />
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={i} transform={`translate(17 ${68 + i * 38})`}>
            <rect width="24" height="24" rx="6" fill={i === 1 ? "#0072FF" : "#EAF3FF"} />
            {i === 1 && <rect x="6" y="6" width="12" height="12" rx="2" fill="#FFFFFF" />}
          </g>
        ))}

        {/* MAIN AREA */}
        <g transform="translate(78 64)">
          {/* heading row */}
          <rect width="142" height="10" rx="5" fill="#061A3A" />
          <rect y="18" width="92" height="6" rx="3" fill="#061A3A" fillOpacity="0.4" />

          {/* big amount */}
          <text x="0" y="68" fontFamily="Inter, system-ui, sans-serif" fontSize="32" fontWeight="800" fill="#061A3A">
            $48,210
          </text>
          {/* trend pill */}
          <g transform="translate(160 42)">
            <rect width="76" height="28" rx="14" fill="#EAF3FF" />
            <path d="M12 18 L17 11 L22 18 Z" fill="#0072FF" />
            <text x="28" y="20" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#0072FF">+18.2%</text>
          </g>

          {/* AREA CHART */}
          <g transform="translate(0 96)">
            <line x1="0" y1="0"  x2="290" y2="0"  stroke="#EEF2F6" />
            <line x1="0" y1="32" x2="290" y2="32" stroke="#EEF2F6" />
            <line x1="0" y1="64" x2="290" y2="64" stroke="#EEF2F6" />
            <line x1="0" y1="96" x2="290" y2="96" stroke="#EEF2F6" />

            {/* area fill */}
            <path
              d="M0,76 L36,62 L72,50 L108,60 L144,42 L180,28 L216,32 L252,18 L288,8 L288,96 L0,96 Z"
              fill="#0072FF" fillOpacity="0.12"
            />
            {/* bars (translucent behind) */}
            <rect x="8"   y="68" width="14" height="28" rx="3" fill="#3A8EFF" fillOpacity="0.32" />
            <rect x="44"  y="56" width="14" height="40" rx="3" fill="#3A8EFF" fillOpacity="0.32" />
            <rect x="80"  y="44" width="14" height="52" rx="3" fill="#3A8EFF" fillOpacity="0.32" />
            <rect x="116" y="56" width="14" height="40" rx="3" fill="#3A8EFF" fillOpacity="0.32" />
            <rect x="152" y="36" width="14" height="60" rx="3" fill="#3A8EFF" fillOpacity="0.4" />
            <rect x="188" y="22" width="14" height="74" rx="3" fill="#0072FF" fillOpacity="0.55" />
            <rect x="224" y="26" width="14" height="70" rx="3" fill="#3A8EFF" fillOpacity="0.4" />
            <rect x="260" y="10" width="14" height="86" rx="3" fill="#0072FF" />

            {/* trendline */}
            <polyline
              fill="none"
              stroke="#0072FF"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              points="0,76 36,62 72,50 108,60 144,42 180,28 216,32 252,18 288,8"
            />
            {[
              [0, 76], [36, 62], [72, 50], [108, 60], [144, 42],
              [180, 28], [216, 32], [252, 18],
            ].map((p, i) => (
              <circle key={i} cx={p[0]} cy={p[1]} r="3" fill="#FFFFFF" stroke="#0072FF" strokeWidth="2" />
            ))}
            {/* pulsing lead point */}
            <g>
              <circle cx="288" cy="8" r="5" fill="#0072FF">
                <animate attributeName="r" values="5;11;5" dur="2.4s" repeatCount="indefinite" />
                <animate attributeName="fill-opacity" values="1;0.15;1" dur="2.4s" repeatCount="indefinite" />
              </circle>
              <circle cx="288" cy="8" r="4" fill="#FFFFFF" stroke="#0072FF" strokeWidth="2.5" />
            </g>
          </g>
        </g>
      </g>

      {/* ── FLOATING KPI CARD (top-right, overlapping hero) ───── */}
      <g transform="translate(394 70)" filter={`url(#${id}-shadow)`}>
        <rect width="172" height="86" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        {/* top accent */}
        <rect x="14" y="14" width="48" height="6" rx="3" fill="#061A3A" fillOpacity="0.5" />
        <text x="14" y="48" fontFamily="Inter, system-ui, sans-serif" fontSize="22" fontWeight="800" fill="#061A3A">
          +18.2%
        </text>
        <g transform="translate(124 16)">
          <rect width="36" height="36" rx="10" fill={`url(#${id}-accent)`} />
          <path d="M10 24 L18 14 L26 24 Z" fill="#FFFFFF" />
        </g>
        {/* tiny inline sparkline */}
        <polyline
          points="14,68 32,64 48,66 64,58 80,60 96,52 112,56 128,46 144,50 160,42"
          fill="none"
          stroke="#0072FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* ── FLOATING TAILORED-PLAN CARD (bottom-right, overlaps) ─ */}
      <g transform="translate(382 388)" filter={`url(#${id}-shadow)`}>
        <rect width="184" height="148" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        {/* top accent bar */}
        <rect x="0" y="0" width="184" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <rect x="16" y="18" width="96" height="8" rx="4" fill="#061A3A" />
        <rect x="16" y="32" width="60" height="6" rx="3" fill="#0072FF" />

        {/* sliders */}
        {[0, 1, 2].map((i) => {
          const fills = [60, 110, 36];
          return (
            <g key={i} transform={`translate(16 ${56 + i * 24})`}>
              <rect width="48" height="6" rx="3" fill="#061A3A" fillOpacity="0.35" y="0" />
              <line x1="60" y1="6" x2="152" y2="6" stroke="#EEF2F6" strokeWidth="5" strokeLinecap="round" />
              <line x1="60" y1="6" x2={60 + fills[i] * 0.7} y2="6" stroke="#0072FF" strokeWidth="5" strokeLinecap="round" />
              <circle cx={60 + fills[i] * 0.7} cy="6" r="6" fill="#FFFFFF" stroke="#0072FF" strokeWidth="2.5" />
            </g>
          );
        })}

        {/* CTA chip */}
        <g transform="translate(16 124)">
          <rect width="152" height="14" rx="7" fill={`url(#${id}-accent)`} />
          <rect x="50" y="4" width="52" height="6" rx="3" fill="#FFFFFF" />
        </g>
      </g>

      {/* ── STACKED PAYMENT CARDS (bottom-left, overlaps) ─────── */}
      <g transform="translate(46 396)">
        <g transform="translate(28 16) rotate(-14 96 60)" filter={`url(#${id}-soft)`}>
          <rect width="190" height="116" rx="16" fill="#3A8EFF" opacity="0.55" />
        </g>
        <g transform="rotate(-6 96 60)" filter={`url(#${id}-shadow)`}>
          <rect width="190" height="116" rx="16" fill={`url(#${id}-navy)`} />
          <rect width="190" height="40" rx="16" fill={`url(#${id}-accent)`} />
          <rect y="22" width="190" height="18" fill="#0072FF" />
          {/* chip */}
          <rect x="16" y="54" width="30" height="22" rx="4" fill="#3A8EFF" />
          <line x1="16" y1="60" x2="46" y2="60" stroke="#061A3A" strokeOpacity="0.6" />
          <line x1="16" y1="68" x2="46" y2="68" stroke="#061A3A" strokeOpacity="0.6" />
          {/* number groups */}
          <rect x="16"  y="84" width="32" height="6" rx="2" fill="#FFFFFF" fillOpacity="0.85" />
          <rect x="56"  y="84" width="32" height="6" rx="2" fill="#FFFFFF" fillOpacity="0.55" />
          <rect x="96"  y="84" width="32" height="6" rx="2" fill="#FFFFFF" fillOpacity="0.55" />
          <rect x="136" y="84" width="38" height="6" rx="2" fill="#FFFFFF" />
          {/* contactless */}
          <g transform="translate(148 54)" stroke="#FFFFFF" strokeWidth="2.2" fill="none" strokeLinecap="round">
            <path d="M0 12 Q6 6 12 12" />
            <path d="M-4 16 Q6 2 16 16" />
          </g>
        </g>
      </g>

      {/* ── FLOATING REVENUE NOTIFICATION (top-left) ─────────── */}
      <g transform="translate(56 78)" filter={`url(#${id}-soft)`}>
        <rect width="180" height="42" rx="21" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(10 7)">
          <circle cx="14" cy="14" r="14" fill={`url(#${id}-accent)`} />
          <path d="M7 14 L12 19 L21 9" stroke="#FFFFFF" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="46" y="20" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Today
        </text>
        <text x="46" y="32" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          + $12,450 revenue
        </text>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="540" cy="270" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="60" cy="340" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.6s" begin="1s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   2) ECOSYSTEM — Run payments smoothly (full stack)
   Hero: big browser-style "Connected Devices" command panel.
   Orbiting around it: mini device chips connected by animated dots.
   ════════════════════════════════════════════════════════════ */
export function EcosystemIllustration({ className = "", transparent = false }) {
  const id = "e3";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Connected payments command center linking terminal, mobile, online checkout, and POS"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        transparent={transparent}
        accents={
          <g>
            <circle cx="80"  cy="120" r="42" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <circle cx="520" cy="480" r="58" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <rect x="40"  y="430" width="60" height="60" rx="16" fill="#FFFFFF" fillOpacity={transparent ? "0.06" : "0.0"} transform="rotate(-10 70 460)" />
            <rect x="498" y="80"  width="60" height="60" rx="16" fill="#0072FF" fillOpacity={transparent ? "0.12" : "0.05"} transform="rotate(14 528 110)" />
          </g>
        }
      />

      {/* HERO — large command panel */}
      <g transform="translate(94 138)" filter={`url(#${id}-shadow)`}>
        <rect width="412" height="276" rx="22" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* top bar */}
        <rect width="412" height="44" rx="22" fill={`url(#${id}-navy)`} />
        <rect y="20" width="412" height="24" fill="#061A3A" />
        <circle cx="22" cy="22" r="5" fill="#0072FF" />
        <circle cx="42" cy="22" r="5" fill="#3A8EFF" />
        <circle cx="62" cy="22" r="5" fill="#FFFFFF" fillOpacity="0.55" />
        <rect x="150" y="14" width="140" height="16" rx="8" fill="#FFFFFF" fillOpacity="0.1" />
        <rect x="162" y="18" width="60" height="8" rx="4" fill="#FFFFFF" fillOpacity="0.6" />
        <g transform="translate(370 14)">
          <rect width="28" height="16" rx="4" fill="#0072FF" />
        </g>

        {/* heading */}
        <g transform="translate(26 62)">
          <rect width="180" height="10" rx="5" fill="#061A3A" />
          <rect y="18" width="110" height="6" rx="3" fill="#061A3A" fillOpacity="0.4" />
        </g>

        {/* device row */}
        <g transform="translate(26 102)">
          {/* device chip 1 */}
          <g>
            <rect width="84" height="86" rx="12" fill="#EAF3FF" />
            {/* terminal mini */}
            <g transform="translate(28 16)">
              <rect width="28" height="40" rx="4" fill={`url(#${id}-navy)`} />
              <rect x="4" y="4" width="20" height="14" rx="2" fill="#0072FF" />
              <rect x="6" y="22" width="6" height="4" rx="1" fill="#FFFFFF" fillOpacity="0.7" />
              <rect x="14" y="22" width="6" height="4" rx="1" fill="#FFFFFF" fillOpacity="0.7" />
              <rect x="6" y="30" width="6" height="4" rx="1" fill="#FFFFFF" fillOpacity="0.55" />
              <rect x="14" y="30" width="6" height="4" rx="1" fill="#FFFFFF" fillOpacity="0.55" />
            </g>
            <rect x="16" y="64" width="52" height="6" rx="3" fill="#061A3A" />
            <rect x="22" y="74" width="40" height="4" rx="2" fill="#0072FF" />
          </g>
          {/* device chip 2 */}
          <g transform="translate(94 0)">
            <rect width="84" height="86" rx="12" fill="#EAF3FF" />
            <g transform="translate(30 14)">
              <rect width="24" height="44" rx="6" fill={`url(#${id}-navy)`} />
              <rect x="2" y="6" width="20" height="32" rx="3" fill="#FFFFFF" />
              <circle cx="12" cy="22" r="6" fill="#0072FF" />
              <rect x="6" y="32" width="12" height="3" rx="1.5" fill="#061A3A" fillOpacity="0.4" />
            </g>
            <rect x="16" y="64" width="52" height="6" rx="3" fill="#061A3A" />
            <rect x="22" y="74" width="40" height="4" rx="2" fill="#0072FF" />
          </g>
          {/* device chip 3 */}
          <g transform="translate(188 0)">
            <rect width="84" height="86" rx="12" fill="#EAF3FF" />
            <g transform="translate(14 22)">
              <rect width="56" height="34" rx="4" fill={`url(#${id}-navy)`} />
              <rect x="3" y="3" width="50" height="28" rx="2" fill="#FFFFFF" />
              <rect x="8" y="8" width="22" height="3" rx="1.5" fill="#061A3A" />
              <rect x="8" y="14" width="36" height="3" rx="1.5" fill="#0072FF" />
              <rect x="8" y="22" width="22" height="6" rx="2" fill="#0072FF" />
              <rect x="-2" y="34" width="60" height="4" rx="2" fill="#061A3A" />
            </g>
            <rect x="16" y="64" width="52" height="6" rx="3" fill="#061A3A" />
            <rect x="22" y="74" width="40" height="4" rx="2" fill="#0072FF" />
          </g>
          {/* device chip 4 — highlighted (active) */}
          <g transform="translate(282 0)">
            <rect width="84" height="86" rx="12" fill={`url(#${id}-accent)`} />
            <g transform="translate(20 14)">
              <rect width="44" height="40" rx="6" fill="#FFFFFF" />
              <rect x="4" y="4" width="36" height="6" rx="3" fill="#061A3A" />
              <rect x="4" y="14" width="20" height="3" rx="1.5" fill="#0072FF" />
              <rect x="4" y="22" width="16" height="10" rx="2" fill="#EAF3FF" />
              <rect x="24" y="22" width="16" height="10" rx="2" fill={`url(#${id}-accent)`} />
            </g>
            <rect x="16" y="64" width="52" height="6" rx="3" fill="#FFFFFF" />
            <rect x="22" y="74" width="40" height="4" rx="2" fill="#FFFFFF" fillOpacity="0.7" />
          </g>
        </g>

        {/* bottom stats row */}
        <g transform="translate(26 208)">
          <rect width="118" height="44" rx="10" fill="#F4F8FF" />
          <rect x="12" y="10" width="50" height="6" rx="3" fill="#061A3A" fillOpacity="0.5" />
          <text x="12" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#061A3A">12,840</text>

          <g transform="translate(128 0)">
            <rect width="118" height="44" rx="10" fill="#F4F8FF" />
            <rect x="12" y="10" width="50" height="6" rx="3" fill="#061A3A" fillOpacity="0.5" />
            <text x="12" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#061A3A">99.98%</text>
          </g>

          <g transform="translate(256 0)">
            <rect width="104" height="44" rx="10" fill={`url(#${id}-accent)`} />
            <rect x="12" y="10" width="50" height="6" rx="3" fill="#FFFFFF" fillOpacity="0.7" />
            <text x="12" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#FFFFFF">+18.2%</text>
          </g>
        </g>
      </g>

      {/* ── ORBIT CONNECTION PATHS + TRAVELING DOTS ──────────── */}
      <g fill="none" strokeLinecap="round" opacity="0.7">
        <path id={`${id}-pTL`} d="M118,170 C50,160 30,80 124,60"  stroke="#0072FF" strokeWidth="2" strokeDasharray="3 7" />
        <path id={`${id}-pTR`} d="M482,170 C560,160 580,80 476,60" stroke="#0072FF" strokeWidth="2" strokeDasharray="3 7" />
        <path id={`${id}-pBL`} d="M118,420 C50,440 50,540 154,540" stroke="#0072FF" strokeWidth="2" strokeDasharray="3 7" />
        <path id={`${id}-pBR`} d="M482,420 C560,440 560,540 446,540" stroke="#0072FF" strokeWidth="2" strokeDasharray="3 7" />
      </g>
      {[
        { p: `${id}-pTL`, d: "3.6s", b: "0s" },
        { p: `${id}-pTR`, d: "4s",   b: "0.6s" },
        { p: `${id}-pBL`, d: "3.8s", b: "1.2s" },
        { p: `${id}-pBR`, d: "3.4s", b: "1.8s" },
      ].map((c) => (
        <circle key={c.p} r="4" fill="#0072FF">
          <animateMotion dur={c.d} repeatCount="indefinite" begin={c.b}>
            <mpath href={`#${c.p}`} />
          </animateMotion>
        </circle>
      ))}

      {/* ── ORBITING DEVICE CHIPS ─────────────────────────────── */}
      {/* TL — terminal chip */}
      <g transform="translate(78 28)" filter={`url(#${id}-soft)`}>
        <rect width="96" height="60" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <rect width="28" height="36" rx="5" fill={`url(#${id}-navy)`} />
          <rect x="4" y="4" width="20" height="10" rx="2" fill="#0072FF" />
          <rect x="6" y="18" width="6" height="4" rx="1" fill="#FFFFFF" fillOpacity="0.7" />
          <rect x="14" y="18" width="6" height="4" rx="1" fill="#FFFFFF" fillOpacity="0.7" />
          <rect x="6" y="26" width="16" height="3" rx="1.5" fill="#0072FF" />
        </g>
        <text x="48" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">Device</text>
        <text x="48" y="42" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#061A3A">Terminal</text>
      </g>
      {/* TR — mobile chip */}
      <g transform="translate(430 28)" filter={`url(#${id}-soft)`}>
        <rect width="96" height="60" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <rect width="24" height="36" rx="5" fill={`url(#${id}-navy)`} />
          <rect x="2" y="5" width="20" height="26" rx="3" fill="#FFFFFF" />
          <circle cx="12" cy="18" r="5" fill="#0072FF" />
          <rect x="4" y="26" width="16" height="3" rx="1.5" fill="#061A3A" fillOpacity="0.4" />
        </g>
        <text x="44" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">Channel</text>
        <text x="44" y="42" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#061A3A">Mobile</text>
      </g>
      {/* BL — online chip */}
      <g transform="translate(78 510)" filter={`url(#${id}-soft)`}>
        <rect width="120" height="60" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 14)">
          <rect width="40" height="28" rx="4" fill={`url(#${id}-navy)`} />
          <rect x="3" y="3" width="34" height="22" rx="2" fill="#FFFFFF" />
          <rect x="6" y="6" width="16" height="3" rx="1.5" fill="#061A3A" />
          <rect x="6" y="12" width="26" height="3" rx="1.5" fill="#0072FF" />
          <rect x="6" y="18" width="14" height="4" rx="2" fill="#0072FF" />
        </g>
        <text x="64" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">Channel</text>
        <text x="64" y="42" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#061A3A">Online</text>
      </g>
      {/* BR — POS chip */}
      <g transform="translate(406 510)" filter={`url(#${id}-soft)`}>
        <rect width="120" height="60" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <rect width="42" height="34" rx="5" fill={`url(#${id}-navy)`} />
          <rect x="3" y="3" width="36" height="28" rx="3" fill="#FFFFFF" />
          <rect x="6" y="6"  width="14" height="3" rx="1.5" fill="#061A3A" />
          <rect x="6" y="12" width="22" height="3" rx="1.5" fill="#0072FF" />
          <rect x="6" y="18" width="10" height="6" rx="2" fill="#EAF3FF" />
          <rect x="18" y="18" width="10" height="6" rx="2" fill={`url(#${id}-accent)`} />
        </g>
        <text x="64" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">Device</text>
        <text x="64" y="42" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#061A3A">POS</text>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="300" cy="80" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="300" cy="520" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.6s" begin="0.8s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   3) ANYWHERE — Mobile payments anywhere your customers are
   Hero: big tilted phone showing Tap-to-Pay with $ amount.
   Orbiting: card terminal peeking, success card, globe pin chip.
   ════════════════════════════════════════════════════════════ */
export function AnywhereIllustration({ className = "", transparent = false }) {
  const id = "a3";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Tap-to-pay phone with floating success card and signal arcs reaching a card terminal"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        transparent={transparent}
        accents={
          <g>
            <circle cx="106" cy="86"  r="42" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <circle cx="510" cy="540" r="46" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <rect x="500" y="80"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity={transparent ? "0.12" : "0.05"} transform="rotate(14 532 112)" />
            <rect x="44"  y="470" width="50" height="50" rx="14" fill="#FFFFFF" fillOpacity={transparent ? "0.06" : "0.0"} transform="rotate(-12 69 495)" />
          </g>
        }
      />

      {/* ── CARD TERMINAL (behind phone, peeking from right) ─── */}
      <g transform="translate(338 188) rotate(8 84 132)" filter={`url(#${id}-shadow)`}>
        <rect width="170" height="270" rx="20" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* screen */}
        <rect x="14" y="14" width="142" height="92" rx="8" fill={`url(#${id}-navy)`} />
        <g transform="translate(70 30)">
          <circle cx="14" cy="14" r="14" fill="#0072FF" />
          <path d="M7 14 L12 19 L21 9" stroke="#FFFFFF" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <rect x="50" y="74" width="70" height="6" rx="3" fill="#FFFFFF" fillOpacity="0.7" />
        <rect x="58" y="86" width="54" height="4" rx="2" fill="#FFFFFF" fillOpacity="0.5" />
        {/* keypad */}
        {[0, 1, 2, 3].map((r) =>
          [0, 1, 2].map((c) => (
            <rect
              key={`pk-${r}-${c}`}
              x={22 + c * 42}
              y={124 + r * 28}
              width="34"
              height="22"
              rx="4"
              fill={(r + c) % 2 === 0 ? "#EAF3FF" : "#EEF2F6"}
            />
          ))
        )}
        <rect x="22" y="246" width="126" height="6" rx="3" fill="#0072FF" />
      </g>

      {/* ── SIGNAL ARCS between phone and terminal ─────────────── */}
      <g fill="none" strokeLinecap="round">
        {[
          { d: "M286 280 Q300 252 314 280", w: 3,   o: 0.95, bg: 0   },
          { d: "M270 270 Q300 222 330 270", w: 3,   o: 0.7,  bg: 0.3 },
          { d: "M254 260 Q300 192 346 260", w: 3,   o: 0.45, bg: 0.6 },
          { d: "M240 250 Q300 162 360 250", w: 3,   o: 0.25, bg: 0.9 },
        ].map((s, i) => (
          <path key={i} d={s.d} stroke="#0072FF" strokeWidth={s.w} opacity={s.o}>
            <animate
              attributeName="opacity"
              values={`${s.o};${s.o * 0.25};${s.o}`}
              dur="2s"
              begin={`${s.bg}s`}
              repeatCount="indefinite"
            />
          </path>
        ))}
      </g>

      {/* ── HERO PHONE (centered-left, tilted) ──────────────── */}
      <g transform="translate(98 174) rotate(-8 78 152)" filter={`url(#${id}-shadow)`}>
        <rect width="156" height="304" rx="26" fill={`url(#${id}-navy)`} />
        <rect x="10" y="30" width="136" height="244" rx="16" fill="#FFFFFF" />
        <rect x="56" y="14" width="44" height="6" rx="3" fill="#061A3A" />

        {/* status row */}
        <g transform="translate(20 42)">
          <rect width="44" height="6" rx="3" fill="#061A3A" />
          <rect y="14" width="80" height="5" rx="2.5" fill="#061A3A" fillOpacity="0.4" />
        </g>

        {/* big amount card */}
        <g transform="translate(20 76)">
          <rect width="116" height="86" rx="14" fill={`url(#${id}-accent)`} />
          <rect x="14" y="14" width="50" height="6" rx="3" fill="#FFFFFF" fillOpacity="0.75" />
          <text
            x="58"
            y="56"
            textAnchor="middle"
            fontFamily="Inter, system-ui, sans-serif"
            fontSize="26"
            fontWeight="800"
            fill="#FFFFFF"
          >
            $24.99
          </text>
          <rect x="22" y="68" width="72" height="4" rx="2" fill="#FFFFFF" fillOpacity="0.55" />
        </g>

        {/* contactless big */}
        <g transform="translate(50 178)" stroke="#0072FF" strokeWidth="3.4" fill="none" strokeLinecap="round">
          <path d="M0 26 Q14 14 28 26">
            <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
          </path>
          <path d="M-10 32 Q14 4 38 32">
            <animate attributeName="opacity" values="0.6;1;0.6" dur="1.8s" begin="0.3s" repeatCount="indefinite" />
          </path>
        </g>

        {/* confirm button */}
        <rect x="20" y="226" width="116" height="22" rx="8" fill="#0072FF" />
        <rect x="50" y="234" width="56" height="6" rx="3" fill="#FFFFFF" />

        {/* home indicator */}
        <rect x="62" y="262" width="32" height="4" rx="2" fill="#061A3A" fillOpacity="0.18" />
      </g>

      {/* ── FLOATING SUCCESS CARD (overlapping phone bottom) ─── */}
      <g transform="translate(72 488)" filter={`url(#${id}-shadow)`}>
        <rect width="232" height="68" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(14 14)">
          <circle cx="20" cy="20" r="20" fill={`url(#${id}-accent)`} />
          <path d="M11 20 L18 27 L29 16" stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="68" y="32" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="700" fill="#061A3A" opacity="0.55">
          Payment confirmed
        </text>
        <text x="68" y="52" fontFamily="Inter, system-ui, sans-serif" fontSize="16" fontWeight="800" fill="#061A3A">
          + $24.99
        </text>
        <g transform="translate(196 22)">
          <rect width="24" height="24" rx="8" fill="#EAF3FF" />
          <path d="M9 12 L11 14 L15 10" stroke="#0072FF" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </g>

      {/* ── GLOBE + LOCATION PIN CHIP (top-right) ──────────────── */}
      <g transform="translate(404 52)" filter={`url(#${id}-shadow)`}>
        <rect width="152" height="100" rx="20" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        {/* globe */}
        <g transform="translate(14 14)">
          <circle cx="36" cy="36" r="34" fill="#EAF3FF" />
          <g stroke="#0072FF" strokeWidth="1.6" fill="none" opacity="0.55">
            <ellipse cx="36" cy="36" rx="34" ry="14" />
            <ellipse cx="36" cy="36" rx="20" ry="34" />
            <line x1="2" y1="36" x2="70" y2="36" />
          </g>
          {/* pulse pin */}
          <g transform="translate(28 14)">
            <circle cx="8" cy="8" r="6" fill="#0072FF" fillOpacity="0.2">
              <animate attributeName="r" values="6;14;6" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="fill-opacity" values="0.4;0;0.4" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <path d="M8 0 C4 0 0 4 0 8 C0 14 8 22 8 22 C8 22 16 14 16 8 C16 4 12 0 8 0 Z" fill={`url(#${id}-accent)`} />
            <circle cx="8" cy="8" r="3" fill="#FFFFFF" />
          </g>
        </g>
        <text x="98" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Anywhere
        </text>
        <text x="98" y="50" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          Live
        </text>
        <g transform="translate(98 58)">
          <circle cx="6" cy="10" r="4" fill="#0072FF" />
          <rect x="14" y="6" width="22" height="4" rx="2" fill="#061A3A" />
          <rect x="14" y="13" width="14" height="3" rx="1.5" fill="#061A3A" fillOpacity="0.4" />
        </g>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="60" cy="320" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="540" cy="340" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="4s" begin="0.6s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   4) MISSION — Vision, goals, business growth
   Hero: large bullseye/target with arrow at center; orbit:
   "Goal" KPI card, ascending bar chart card, achievement badge.
   ════════════════════════════════════════════════════════════ */
export function MissionIllustration({ className = "" }) {
  const id = "m1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Mission and vision — target with arrow hitting center and growth chart"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        accents={
          <g>
            <circle cx="92"  cy="106" r="40" fill="#0072FF" fillOpacity="0.07" />
            <circle cx="510" cy="520" r="56" fill="#0072FF" fillOpacity="0.08" />
            <rect x="498" y="78"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity="0.05" transform="rotate(14 530 110)" />
            <rect x="56"  y="470" width="56" height="56" rx="16" fill="#061A3A" fillOpacity="0.05" transform="rotate(-10 84 498)" />
          </g>
        }
      />

      {/* HERO — bullseye target */}
      <g transform="translate(160 152)" filter={`url(#${id}-shadow)`}>
        {/* outer rings */}
        <circle cx="140" cy="140" r="138" fill="#FFFFFF" />
        <circle cx="140" cy="140" r="138" fill="none" stroke="#061A3A" strokeOpacity="0.06" />
        <circle cx="140" cy="140" r="112" fill="#EAF3FF" />
        <circle cx="140" cy="140" r="86"  fill="#3A8EFF" fillOpacity="0.20" />
        <circle cx="140" cy="140" r="58"  fill={`url(#${id}-accent)`} />
        <circle cx="140" cy="140" r="32"  fill={`url(#${id}-navy)`} />
        {/* crosshair lines */}
        <line x1="140" y1="2"   x2="140" y2="38"  stroke="#061A3A" strokeOpacity="0.18" strokeWidth="2" strokeDasharray="3 5" />
        <line x1="140" y1="242" x2="140" y2="278" stroke="#061A3A" strokeOpacity="0.18" strokeWidth="2" strokeDasharray="3 5" />
        <line x1="2"   y1="140" x2="38"  y2="140" stroke="#061A3A" strokeOpacity="0.18" strokeWidth="2" strokeDasharray="3 5" />
        <line x1="242" y1="140" x2="278" y2="140" stroke="#061A3A" strokeOpacity="0.18" strokeWidth="2" strokeDasharray="3 5" />
        {/* pulse ring */}
        <circle cx="140" cy="140" r="58" fill="none" stroke="#FFFFFF" strokeOpacity="0.7" strokeWidth="2">
          <animate attributeName="r" values="58;90;58" dur="3s" repeatCount="indefinite" />
          <animate attributeName="stroke-opacity" values="0.6;0;0.6" dur="3s" repeatCount="indefinite" />
        </circle>

        {/* ARROW hitting center — angled from bottom-right */}
        <g transform="translate(140 140) rotate(-32)">
          <rect x="-160" y="-3" width="148" height="6" rx="3" fill={`url(#${id}-navy)`} />
          <polygon points="-12,-12 8,0 -12,12" fill="#0072FF" />
          {/* fletching */}
          <polygon points="-160,-7 -150,0 -160,7 -170,0" fill="#0072FF" />
          <polygon points="-150,-9 -140,0 -150,9 -160,0" fill="#3A8EFF" />
        </g>
        {/* center dot */}
        <circle cx="140" cy="140" r="6" fill="#FFFFFF" />
      </g>

      {/* GROWTH CHART CARD — bottom-right */}
      <g transform="translate(360 388)" filter={`url(#${id}-shadow)`}>
        <rect width="204" height="148" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="204" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <rect x="14" y="18" width="90" height="8" rx="4" fill="#061A3A" />
        <rect x="14" y="32" width="50" height="6" rx="3" fill="#0072FF" />
        <text x="14" y="68" fontFamily="Inter, system-ui, sans-serif" fontSize="18" fontWeight="800" fill="#061A3A">+18.2%</text>
        {/* ascending bars */}
        <g transform="translate(14 80)">
          <rect x="0"   y="38" width="16" height="20" rx="3" fill="#EAF3FF" />
          <rect x="22"  y="28" width="16" height="30" rx="3" fill="#3A8EFF" fillOpacity="0.55" />
          <rect x="44"  y="18" width="16" height="40" rx="3" fill="#3A8EFF" />
          <rect x="66"  y="22" width="16" height="36" rx="3" fill="#3A8EFF" fillOpacity="0.55" />
          <rect x="88"  y="10" width="16" height="48" rx="3" fill="#0072FF" />
          <rect x="110" y="2"  width="16" height="56" rx="3" fill={`url(#${id}-navy)`} />
          <polyline
            points="8,46 30,36 52,24 74,30 96,16 118,8"
            fill="none" stroke="#0072FF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          />
          <circle cx="118" cy="8" r="4" fill="#FFFFFF" stroke="#0072FF" strokeWidth="2">
            <animate attributeName="r" values="4;7;4" dur="2.2s" repeatCount="indefinite" />
          </circle>
        </g>
        <rect x="142" y="40" width="50" height="22" rx="11" fill="#EAF3FF" />
        <text x="151" y="55" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#0072FF">GOAL</text>
      </g>

      {/* MISSION BADGE — top-left */}
      <g transform="translate(54 70)" filter={`url(#${id}-shadow)`}>
        <rect width="200" height="60" rx="30" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <circle cx="18" cy="18" r="18" fill={`url(#${id}-accent)`} />
          <path
            d="M18 6 L21 14 L29 14 L23 19 L25 27 L18 22 L11 27 L13 19 L7 14 L15 14 Z"
            fill="#FFFFFF"
          />
        </g>
        <text x="56" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Our mission
        </text>
        <text x="56" y="46" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          Build for owners
        </text>
      </g>

      {/* ACHIEVEMENT CHECK CHIP — bottom-left */}
      <g transform="translate(60 444)" filter={`url(#${id}-shadow)`}>
        <rect width="170" height="58" rx="29" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(10 9)">
          <circle cx="20" cy="20" r="20" fill={`url(#${id}-accent)`} />
          <path d="M11 20 L18 27 L29 16" stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="60" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Target hit
        </text>
        <text x="60" y="44" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          Honest payments
        </text>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="540" cy="120" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="270" cy="530" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.8s" begin="1s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   5) SECURITY — Secure payments / why choose / trust
   Hero: large shield with check; orbit: padlock chip, encrypted
   data stream, "PCI · EMV" badges.
   ════════════════════════════════════════════════════════════ */
export function SecurityIllustration({ className = "", transparent = false }) {
  const id = "s1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Secure payments — shield with checkmark and encrypted data"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        transparent={transparent}
        accents={
          <g>
            <circle cx="86"  cy="120" r="42" fill="#0072FF" fillOpacity="0.07" />
            <circle cx="514" cy="510" r="58" fill="#0072FF" fillOpacity="0.07" />
            <rect x="500" y="76"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity="0.05" transform="rotate(12 532 108)" />
            <rect x="48"  y="466" width="56" height="56" rx="16" fill="#061A3A" fillOpacity="0.05" transform="rotate(-10 76 494)" />
          </g>
        }
      />

      {/* Concentric pulse rings */}
      <g transform="translate(300 300)">
        <circle r="170" fill="none" stroke="#0072FF" strokeOpacity="0.18" strokeWidth="2" strokeDasharray="4 8">
          <animate attributeName="r" values="170;200;170" dur="4s" repeatCount="indefinite" />
        </circle>
        <circle r="200" fill="none" stroke="#0072FF" strokeOpacity="0.12" strokeWidth="2" strokeDasharray="4 10">
          <animate attributeName="r" values="200;230;200" dur="4s" begin="1.5s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* HERO SHIELD */}
      <g transform="translate(204 134)" filter={`url(#${id}-shadow)`}>
        {/* outer shield */}
        <path
          d="M96 0 L188 32 L188 152 C188 220 152 268 96 304 C40 268 4 220 4 152 L4 32 Z"
          fill={`url(#${id}-navy)`}
        />
        {/* inner glow */}
        <path
          d="M96 18 L172 44 L172 152 C172 210 144 250 96 280 C48 250 20 210 20 152 L20 44 Z"
          fill="#0A2552"
        />
        <path
          d="M96 18 L172 44 L172 152 C172 210 144 250 96 280 C48 250 20 210 20 152 L20 44 Z"
          fill={`url(#${id}-accent)`}
          fillOpacity="0.18"
        />
        {/* big check */}
        <path
          d="M58 152 L92 184 L138 124"
          stroke="#FFFFFF"
          strokeWidth="10"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* accent glow under check */}
        <circle cx="96" cy="158" r="80" fill="#0072FF" fillOpacity="0.10" />
        {/* tiny lock at top */}
        <g transform="translate(82 48)">
          <rect x="0" y="10" width="28" height="22" rx="4" fill="#FFFFFF" />
          <path d="M5 10 V6 a9 9 0 0 1 18 0 V10" stroke="#FFFFFF" strokeWidth="3" fill="none" />
          <circle cx="14" cy="21" r="3" fill="#0072FF" />
        </g>
      </g>

      {/* ENCRYPTED DATA STREAM — top-right */}
      <g transform="translate(404 80)" filter={`url(#${id}-shadow)`}>
        <rect width="170" height="92" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="170" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <rect x="14" y="18" width="74" height="6" rx="3" fill="#061A3A" />
        <rect x="14" y="30" width="44" height="4" rx="2" fill="#0072FF" />
        {/* data lines with hashing */}
        <g transform="translate(14 44)" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fill="#061A3A" fillOpacity="0.65">
          <text y="10">●●●● ●●●● 4112</text>
          <text y="24">a7 9c 1f 03 e2</text>
          <text y="38">SHA-256 ✓ TLS 1.3</text>
        </g>
      </g>

      {/* PCI / EMV BADGES — bottom-left */}
      <g transform="translate(56 442)" filter={`url(#${id}-shadow)`}>
        <rect width="200" height="100" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <text x="14" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Certified secure
        </text>
        <g transform="translate(14 38)">
          <rect width="56" height="48" rx="10" fill="#EAF3FF" />
          <text x="28" y="22" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#0072FF">PCI</text>
          <text x="28" y="36" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#061A3A">DSS</text>
        </g>
        <g transform="translate(76 38)">
          <rect width="56" height="48" rx="10" fill={`url(#${id}-accent)`} />
          <text x="28" y="22" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#FFFFFF">EMV</text>
          <text x="28" y="36" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#FFFFFF" fillOpacity="0.85">CHIP</text>
        </g>
        <g transform="translate(138 38)">
          <rect width="56" height="48" rx="10" fill="#EAF3FF" />
          <text x="28" y="22" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#0072FF">TLS</text>
          <text x="28" y="36" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#061A3A">1.3</text>
        </g>
      </g>

      {/* TINY KEY CARD — top-left */}
      <g transform="translate(56 78)" filter={`url(#${id}-shadow)`}>
        <rect width="180" height="42" rx="21" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(10 7)">
          <circle cx="14" cy="14" r="14" fill={`url(#${id}-accent)`} />
          <rect x="9" y="11" width="10" height="9" rx="2" fill="#FFFFFF" />
          <path d="M11 11 V8 a3 3 0 0 1 6 0 V11" stroke="#FFFFFF" strokeWidth="1.5" fill="none" />
        </g>
        <text x="46" y="20" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Encrypted
        </text>
        <text x="46" y="32" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          End-to-end
        </text>
      </g>

      <g>
        <circle cx="540" cy="280" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="64" cy="300" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.8s" begin="0.8s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   6) PROCESS — Onboarding / setup steps
   Hero: vertical checklist panel with 4 numbered steps + checks;
   orbit: gear card, "Setup complete" success card.
   ════════════════════════════════════════════════════════════ */
export function ProcessIllustration({ className = "" }) {
  const id = "p1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Onboarding process — step-by-step checklist with completed setup"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        accents={
          <g>
            <circle cx="80"  cy="100" r="42" fill="#0072FF" fillOpacity="0.07" />
            <circle cx="520" cy="520" r="56" fill="#0072FF" fillOpacity="0.07" />
            <rect x="500" y="76"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity="0.05" transform="rotate(14 532 108)" />
            <rect x="50"  y="470" width="56" height="56" rx="16" fill="#061A3A" fillOpacity="0.05" transform="rotate(-10 78 498)" />
          </g>
        }
      />

      {/* HERO — checklist panel */}
      <g transform="translate(118 102)" filter={`url(#${id}-shadow)`}>
        <rect width="364" height="396" rx="22" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* header bar */}
        <rect width="364" height="56" rx="22" fill={`url(#${id}-navy)`} />
        <rect y="32" width="364" height="24" fill="#061A3A" />
        <g transform="translate(20 18)">
          <circle cx="10" cy="10" r="10" fill={`url(#${id}-accent)`} />
          <path d="M5 10 L9 14 L15 6" stroke="#FFFFFF" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="44" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#FFFFFF">Setup Checklist</text>
        <text x="280" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="700" fill="#FFFFFF" opacity="0.6">3 of 4</text>

        {/* steps */}
        {[
          { i: 1, title: "Review current setup",       done: true },
          { i: 2, title: "Compare your statement",     done: true },
          { i: 3, title: "Confirm equipment & pricing", done: true },
          { i: 4, title: "Go live with Swipetronics", done: false },
        ].map((step, idx) => (
          <g key={step.i} transform={`translate(20 ${78 + idx * 76})`}>
            {/* connector line between steps */}
            {idx < 3 && (
              <line x1="28" y1="56" x2="28" y2="76" stroke={step.done ? "#0072FF" : "#EAF3FF"} strokeWidth="2" />
            )}
            {/* number/check circle */}
            {step.done ? (
              <g>
                <circle cx="28" cy="28" r="22" fill={`url(#${id}-accent)`} />
                <path d="M18 28 L25 35 L38 22" stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            ) : (
              <g>
                <circle cx="28" cy="28" r="22" fill="#FFFFFF" stroke="#0072FF" strokeWidth="2.5" strokeDasharray="3 5">
                  <animateTransform attributeName="transform" type="rotate" from="0 28 28" to="360 28 28" dur="6s" repeatCount="indefinite" />
                </circle>
                <text x="28" y="33" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#0072FF">{step.i}</text>
              </g>
            )}
            {/* text card */}
            <g transform="translate(60 6)">
              <rect width="270" height="44" rx="10" fill={step.done ? "#EAF3FF" : "#F4F8FF"} />
              <rect x="14" y="10" width="120" height="8" rx="4" fill="#061A3A" />
              <rect x="14" y="24" width="180" height="6" rx="3" fill="#061A3A" fillOpacity="0.45" />
              {step.done && (
                <g transform="translate(232 12)">
                  <rect width="26" height="20" rx="6" fill="#0072FF" />
                  <text x="13" y="14" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">DONE</text>
                </g>
              )}
            </g>
          </g>
        ))}

        {/* CTA bar at bottom */}
        <g transform="translate(20 376)">
          <rect width="324" height="12" rx="6" fill="#EEF2F6" />
          <rect width="240" height="12" rx="6" fill={`url(#${id}-accent)`}>
            <animate attributeName="width" values="180;260;240" dur="3s" repeatCount="indefinite" />
          </rect>
        </g>
      </g>

      {/* FLOATING SUCCESS CARD — top-right */}
      <g transform="translate(412 70)" filter={`url(#${id}-shadow)`}>
        <rect width="156" height="80" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(14 14)">
          <circle cx="22" cy="22" r="22" fill={`url(#${id}-accent)`} />
          <path d="M12 22 L20 30 L34 14" stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="68" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Setup status
        </text>
        <text x="68" y="54" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#061A3A">
          75% done
        </text>
      </g>

      {/* GEAR CHIP — bottom-right */}
      <g transform="translate(414 482)" filter={`url(#${id}-shadow)`}>
        <rect width="154" height="58" rx="29" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <circle cx="17" cy="17" r="17" fill={`url(#${id}-accent)`} />
          {/* gear */}
          <g transform="translate(17 17)">
            <g fill="#FFFFFF">
              <rect x="-2" y="-12" width="4" height="6" rx="1" />
              <rect x="-2" y="6"   width="4" height="6" rx="1" />
              <rect x="-12" y="-2" width="6" height="4" rx="1" />
              <rect x="6"   y="-2" width="6" height="4" rx="1" />
            </g>
            <circle r="6" fill="#FFFFFF" />
            <circle r="3" fill={`url(#${id}-accent)`} />
            <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="8s" repeatCount="indefinite" />
          </g>
        </g>
        <text x="48" y="24" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Onboarding
        </text>
        <text x="48" y="42" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          Simple &amp; guided
        </text>
      </g>

      <g>
        <circle cx="60" cy="320" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="540" cy="280" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.8s" begin="0.8s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   7) RESOURCES — Learning / articles / guides
   Hero: open document/article panel with text lines + chart;
   orbit: bookmark, search pill, "5 min read" badge.
   ════════════════════════════════════════════════════════════ */
export function ResourcesIllustration({ className = "", transparent = false }) {
  const id = "r1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Resources and articles — open guide document with reading aids"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        transparent={transparent}
        accents={
          <g>
            <circle cx="80"  cy="110" r="44" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <circle cx="518" cy="510" r="58" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <rect x="496" y="84"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity={transparent ? "0.12" : "0.05"} transform="rotate(14 528 116)" />
            <rect x="50"  y="468" width="56" height="56" rx="16" fill="#FFFFFF" fillOpacity={transparent ? "0.06" : "0.0"} transform="rotate(-10 78 496)" />
          </g>
        }
      />

      {/* Behind document — supplementary page peeking */}
      <g transform="translate(140 158) rotate(-6 170 158)" filter={`url(#${id}-soft)`}>
        <rect width="320" height="316" rx="20" fill="#FFFFFF" opacity="0.7" />
      </g>

      {/* HERO — open article document */}
      <g transform="translate(122 138)" filter={`url(#${id}-shadow)`}>
        <rect width="356" height="324" rx="20" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* top accent + tag */}
        <rect x="0" y="0" width="356" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <g transform="translate(24 22)">
          <rect width="80" height="22" rx="11" fill="#EAF3FF" />
          <text x="40" y="15" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#0072FF">GUIDES</text>
        </g>
        <g transform="translate(116 22)">
          <rect width="90" height="22" rx="11" fill="#F4F8FF" />
          <circle cx="14" cy="11" r="4" fill="#0072FF" />
          <text x="24" y="15" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A">5 min read</text>
        </g>

        {/* article heading */}
        <rect x="24" y="60" width="280" height="10" rx="5" fill="#061A3A" />
        <rect x="24" y="78" width="170" height="10" rx="5" fill="#061A3A" />

        {/* image placeholder */}
        <g transform="translate(24 104)">
          <rect width="308" height="80" rx="10" fill={`url(#${id}-accent)`} />
          {/* mini chart inside */}
          <polyline
            points="20,62 60,52 100,46 140,38 180,42 220,28 260,22 290,12"
            fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          />
          <circle cx="290" cy="12" r="4" fill="#FFFFFF" />
          <rect x="20" y="14" width="80" height="6" rx="3" fill="#FFFFFF" fillOpacity="0.75" />
          <rect x="20" y="26" width="40" height="4" rx="2" fill="#FFFFFF" fillOpacity="0.5" />
        </g>

        {/* body paragraph lines */}
        {[200, 218, 236, 254, 272].map((y, i) => (
          <rect
            key={i}
            x="24"
            y={y}
            width={i === 4 ? 180 : i === 2 ? 260 : 308}
            height="5"
            rx="2.5"
            fill="#061A3A"
            fillOpacity={0.35 + i * 0.05}
          />
        ))}

        {/* highlighter band */}
        <rect x="22" y="232" width="184" height="11" rx="3" fill="#0072FF" fillOpacity="0.15" />

        {/* CTA bar */}
        <g transform="translate(24 292)">
          <rect width="120" height="20" rx="6" fill={`url(#${id}-accent)`} />
          <rect x="38" y="6" width="44" height="6" rx="3" fill="#FFFFFF" />
        </g>
      </g>

      {/* BOOKMARK BADGE — top-right floating */}
      <g transform="translate(440 92)" filter={`url(#${id}-shadow)`}>
        <rect width="120" height="80" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 14)">
          <rect width="32" height="44" rx="4" fill={`url(#${id}-accent)`} />
          <path d="M0 44 L16 36 L32 44 V52 H0 Z" fill={`url(#${id}-accent)`} />
        </g>
        <text x="54" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Saved
        </text>
        <text x="54" y="52" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          14 articles
        </text>
      </g>

      {/* SEARCH PILL — bottom-left floating */}
      <g transform="translate(56 462)" filter={`url(#${id}-shadow)`}>
        <rect width="226" height="50" rx="25" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(18 17)">
          <circle cx="8" cy="8" r="6" fill="none" stroke="#0072FF" strokeWidth="2.2" />
          <line x1="12" y1="12" x2="17" y2="17" stroke="#0072FF" strokeWidth="2.2" strokeLinecap="round" />
        </g>
        <text x="50" y="30" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="700" fill="#061A3A" opacity="0.55">
          Search guides
        </text>
        <rect x="166" y="17" width="46" height="16" rx="8" fill={`url(#${id}-accent)`} />
        <text x="189" y="29" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">Find</text>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="60" cy="320" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="544" cy="320" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="4s" begin="0.8s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   8) PARTNERSHIP — Collaboration / partner program / growth together
   Hero: two big interlocking ring nodes connected by gradient bar
   (abstract handshake); orbit: shared dashboard tile, revenue
   share card, "Partner" badge.
   ════════════════════════════════════════════════════════════ */
export function PartnershipIllustration({ className = "", transparent = false }) {
  const id = "pt1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Partnership — two connected business nodes sharing growth and revenue"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        transparent={transparent}
        accents={
          <g>
            <circle cx="80"  cy="110" r="42" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <circle cx="514" cy="510" r="58" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <rect x="500" y="76"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity={transparent ? "0.12" : "0.05"} transform="rotate(14 532 108)" />
            <rect x="50"  y="468" width="56" height="56" rx="16" fill="#FFFFFF" fillOpacity={transparent ? "0.06" : "0.0"} transform="rotate(-10 78 496)" />
          </g>
        }
      />

      {/* CONNECTION BAR — moving dots between the two nodes */}
      <g>
        <path id={`${id}-link`} d="M214,300 L386,300" fill="none" stroke="#0072FF" strokeOpacity="0.55" strokeWidth="3" strokeDasharray="3 7" />
        {[0, 1, 2].map((i) => (
          <circle key={i} r="4" fill="#0072FF">
            <animateMotion dur="2.6s" begin={`${i * 0.7}s`} repeatCount="indefinite">
              <mpath href={`#${id}-link`} />
            </animateMotion>
          </circle>
        ))}
      </g>

      {/* LEFT NODE — "Your business" */}
      <g transform="translate(76 200)" filter={`url(#${id}-shadow)`}>
        <circle cx="100" cy="100" r="100" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.06" />
        <circle cx="100" cy="100" r="82"  fill="#EAF3FF" />
        <circle cx="100" cy="100" r="60"  fill={`url(#${id}-accent)`} />
        {/* glyph: storefront */}
        <g transform="translate(70 78)">
          <rect width="60" height="42" rx="3" fill="#FFFFFF" />
          <path d="M-2 12 L62 12 L60 0 L0 0 Z" fill="#FFFFFF" />
          <rect x="6" y="2" width="10" height="8" rx="1" fill="#0072FF" fillOpacity="0.7" />
          <rect x="20" y="2" width="10" height="8" rx="1" fill="#0072FF" fillOpacity="0.7" />
          <rect x="34" y="2" width="10" height="8" rx="1" fill="#0072FF" fillOpacity="0.7" />
          <rect x="48" y="2" width="10" height="8" rx="1" fill="#0072FF" fillOpacity="0.7" />
          {/* door */}
          <rect x="22" y="18" width="16" height="24" rx="2" fill="#061A3A" />
          <circle cx="34" cy="30" r="1.5" fill="#FFFFFF" />
        </g>
        {/* label pill */}
        <g transform="translate(50 192)">
          <rect width="100" height="22" rx="11" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
          <circle cx="14" cy="11" r="4" fill="#0072FF" />
          <text x="26" y="15" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#061A3A">YOUR BUSINESS</text>
        </g>
      </g>

      {/* RIGHT NODE — "Swipetronics" */}
      <g transform="translate(324 200)" filter={`url(#${id}-shadow)`}>
        <circle cx="100" cy="100" r="100" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.06" />
        <circle cx="100" cy="100" r="82"  fill="#EAF3FF" />
        <circle cx="100" cy="100" r="60"  fill={`url(#${id}-navy)`} />
        {/* glyph: connected dots/network */}
        <g transform="translate(100 100)" stroke="#FFFFFF" strokeWidth="2.4" fill="#FFFFFF">
          <circle r="8" fill={`url(#${id}-accent)`} stroke="none" />
          <circle cx="-26" cy="-18" r="6" />
          <circle cx="26"  cy="-18" r="6" />
          <circle cx="-26" cy="18"  r="6" />
          <circle cx="26"  cy="18"  r="6" />
          <line x1="0" y1="0" x2="-26" y2="-18" />
          <line x1="0" y1="0" x2="26"  y2="-18" />
          <line x1="0" y1="0" x2="-26" y2="18"  />
          <line x1="0" y1="0" x2="26"  y2="18"  />
        </g>
        <g transform="translate(50 192)">
          <rect width="100" height="22" rx="11" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
          <circle cx="14" cy="11" r="4" fill="#0072FF" />
          <text x="26" y="15" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#061A3A">SWIPETRONICS</text>
        </g>
      </g>

      {/* TOP FLOATING — Shared growth chart */}
      <g transform="translate(192 56)" filter={`url(#${id}-shadow)`}>
        <rect width="216" height="84" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="216" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <rect x="16" y="18" width="100" height="8" rx="4" fill="#061A3A" />
        <rect x="16" y="32" width="60" height="6" rx="3" fill="#0072FF" />
        {/* tiny shared chart */}
        <polyline
          points="16,72 40,62 64,58 88,52 112,42 136,46 160,34 184,28 200,18"
          fill="none" stroke="#0072FF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
        />
        <circle cx="200" cy="18" r="4" fill="#FFFFFF" stroke="#0072FF" strokeWidth="2" />
        <g transform="translate(168 44)">
          <rect width="36" height="16" rx="8" fill="#EAF3FF" />
          <text x="18" y="12" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#0072FF">+24%</text>
        </g>
      </g>

      {/* BOTTOM-LEFT — Revenue share card */}
      <g transform="translate(60 482)" filter={`url(#${id}-shadow)`}>
        <rect width="220" height="62" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(14 14)">
          <circle cx="18" cy="18" r="18" fill={`url(#${id}-accent)`} />
          <text x="18" y="23" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="16" fontWeight="800" fill="#FFFFFF">%</text>
        </g>
        <text x="64" y="30" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Residuals
        </text>
        <text x="64" y="48" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          Transparent payouts
        </text>
      </g>

      {/* BOTTOM-RIGHT — Handshake glyph chip */}
      <g transform="translate(320 482)" filter={`url(#${id}-shadow)`}>
        <rect width="220" height="62" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(14 14)">
          <circle cx="18" cy="18" r="18" fill={`url(#${id}-navy)`} />
          <g transform="translate(7 12)" stroke="#FFFFFF" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M0 8 L4 4 L8 8 L12 4 L16 8 L20 4 L22 8" />
            <path d="M0 8 L0 6 M22 8 L22 6" />
          </g>
        </g>
        <text x="64" y="30" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Long-term
        </text>
        <text x="64" y="48" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          Partner program
        </text>
      </g>

      <g>
        <circle cx="540" cy="120" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="60" cy="360" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.8s" begin="0.8s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   9) CONTACT — Get in touch / customer support
   Hero: large chat conversation panel; orbit: live support
   agent chip, phone + email contact card, "Replies fast" badge.
   ════════════════════════════════════════════════════════════ */
export function ContactIllustration({ className = "", transparent = false }) {
  const id = "c1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Contact Swipetronics — chat conversation with live merchant support"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        transparent={transparent}
        accents={
          <g>
            <circle cx="98"  cy="100" r="44" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <circle cx="514" cy="514" r="56" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <rect x="498" y="78"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity={transparent ? "0.12" : "0.05"} transform="rotate(14 530 110)" />
            <rect x="48"  y="468" width="56" height="56" rx="16" fill="#FFFFFF" fillOpacity={transparent ? "0.06" : "0.0"} transform="rotate(-12 76 496)" />
          </g>
        }
      />

      {/* HERO — chat conversation panel */}
      <g transform="translate(120 116)" filter={`url(#${id}-shadow)`}>
        <rect width="360" height="360" rx="22" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* top bar */}
        <rect width="360" height="56" rx="22" fill={`url(#${id}-navy)`} />
        <rect y="32" width="360" height="24" fill="#061A3A" />
        <g transform="translate(20 18)">
          <circle cx="10" cy="10" r="10" fill={`url(#${id}-accent)`} />
          {/* headset glyph */}
          <g transform="translate(2 2)" stroke="#FFFFFF" strokeWidth="1.8" fill="none" strokeLinecap="round">
            <path d="M2 11 V9 a6 6 0 0 1 12 0 V11" />
            <rect x="1" y="11" width="3" height="5" rx="1" fill="#FFFFFF" stroke="none" />
            <rect x="12" y="11" width="3" height="5" rx="1" fill="#FFFFFF" stroke="none" />
          </g>
        </g>
        <text x="44" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#FFFFFF">Merchant Support</text>
        <g transform="translate(286 22)">
          <circle cx="6" cy="6" r="6" fill="#3A8EFF">
            <animate attributeName="fill-opacity" values="1;0.45;1" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <text x="18" y="10" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#FFFFFF" opacity="0.85">Live</text>
        </g>

        {/* INCOMING BUBBLE 1 (left - from support) */}
        <g transform="translate(22 78)">
          <circle cx="14" cy="22" r="14" fill={`url(#${id}-accent)`} />
          <path d="M9 22 L13 26 L20 18" stroke="#FFFFFF" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <g transform="translate(38 0)">
            <rect width="220" height="62" rx="14" fill="#EAF3FF" />
            <rect x="14" y="14" width="190" height="6" rx="3" fill="#061A3A" fillOpacity="0.65" />
            <rect x="14" y="26" width="160" height="6" rx="3" fill="#061A3A" fillOpacity="0.45" />
            <rect x="14" y="38" width="120" height="6" rx="3" fill="#061A3A" fillOpacity="0.35" />
            <rect x="14" y="50" width="40"  height="4" rx="2" fill="#0072FF" fillOpacity="0.7" />
          </g>
        </g>

        {/* OUTGOING BUBBLE (right - from user) */}
        <g transform="translate(108 154)">
          <rect width="216" height="48" rx="14" fill={`url(#${id}-accent)`} />
          <rect x="14" y="14" width="160" height="6" rx="3" fill="#FFFFFF" fillOpacity="0.95" />
          <rect x="14" y="26" width="120" height="6" rx="3" fill="#FFFFFF" fillOpacity="0.7" />
          <rect x="14" y="38" width="40"  height="3" rx="1.5" fill="#FFFFFF" fillOpacity="0.5" />
        </g>

        {/* INCOMING BUBBLE 2 + typing indicator */}
        <g transform="translate(22 218)">
          <circle cx="14" cy="22" r="14" fill={`url(#${id}-accent)`} />
          <path d="M9 22 L13 26 L20 18" stroke="#FFFFFF" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <g transform="translate(38 0)">
            <rect width="200" height="52" rx="14" fill="#EAF3FF" />
            <rect x="14" y="14" width="160" height="6" rx="3" fill="#061A3A" fillOpacity="0.65" />
            <rect x="14" y="26" width="120" height="6" rx="3" fill="#061A3A" fillOpacity="0.45" />
            <rect x="14" y="38" width="60"  height="4" rx="2" fill="#0072FF" fillOpacity="0.7" />
          </g>
        </g>

        {/* TYPING bubble (animated) */}
        <g transform="translate(60 284)">
          <rect width="64" height="26" rx="13" fill="#EAF3FF" />
          {[0, 1, 2].map((i) => (
            <circle key={i} cx={18 + i * 14} cy="13" r="3" fill="#0072FF">
              <animate
                attributeName="fill-opacity"
                values="0.25;1;0.25"
                dur="1.2s"
                begin={`${i * 0.18}s`}
                repeatCount="indefinite"
              />
            </circle>
          ))}
        </g>

        {/* INPUT BAR at bottom */}
        <g transform="translate(20 326)">
          <rect width="320" height="22" rx="11" fill="#F4F8FF" stroke="#061A3A" strokeOpacity="0.08" />
          <rect x="14" y="8" width="200" height="6" rx="3" fill="#061A3A" fillOpacity="0.35" />
          <g transform="translate(286 3)">
            <rect width="22" height="16" rx="8" fill={`url(#${id}-accent)`} />
            {/* send arrow */}
            <path d="M7 8 L15 8 M12 5 L15 8 L12 11" stroke="#FFFFFF" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </g>
        </g>
      </g>

      {/* FLOATING — 24/7 SUPPORT badge (top-right) */}
      <g transform="translate(412 64)" filter={`url(#${id}-shadow)`}>
        <rect width="158" height="76" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <circle cx="22" cy="22" r="22" fill={`url(#${id}-accent)`} />
          {/* phone glyph */}
          <path
            d="M16 14 C15 14 14 15 14 16 L14 18 C14 25 19 30 26 30 L28 30 C29 30 30 29 30 28 L30 25 C30 24 29 23 28 23 L26 23 L24 25 C22 24 20 22 19 20 L21 18 L21 16 C21 15 20 14 19 14 Z"
            fill="#FFFFFF"
          />
        </g>
        <text x="68" y="30" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Real human help
        </text>
        <text x="68" y="50" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          24/7 support
        </text>
        <g transform="translate(68 56)">
          <circle cx="4" cy="6" r="4" fill="#0072FF">
            <animate attributeName="fill-opacity" values="1;0.3;1" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <text x="12" y="10" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">Online now</text>
        </g>
      </g>

      {/* FLOATING — CONTACT METHODS card (bottom-left) */}
      <g transform="translate(46 478)" filter={`url(#${id}-shadow)`}>
        <rect width="200" height="66" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(14 14)">
          <rect width="38" height="38" rx="10" fill="#EAF3FF" />
          {/* phone */}
          <path d="M14 12 C12 12 11 13 11 15 L11 17 C11 23 17 29 23 29 L25 29 C27 29 28 28 28 26 L28 23 C28 22 27 21 25 21 L23 21 L21 23 C19 22 18 21 17 19 L19 17 L19 15 C19 13 18 12 16 12 Z" fill="#0072FF" />
        </g>
        <g transform="translate(58 14)">
          <rect width="38" height="38" rx="10" fill="#EAF3FF" />
          {/* envelope */}
          <rect x="9" y="14" width="20" height="14" rx="2" fill="#0072FF" />
          <path d="M9 14 L19 22 L29 14" stroke="#FFFFFF" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
        </g>
        <g transform="translate(102 14)">
          <rect width="38" height="38" rx="10" fill={`url(#${id}-accent)`} />
          {/* chat bubble */}
          <path d="M10 14 H28 V24 H22 L18 28 V24 H10 Z" fill="#FFFFFF" />
        </g>
        <text x="148" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">Pick</text>
        <text x="148" y="44" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#061A3A">a channel</text>
      </g>

      {/* FLOATING — "Replies in 1h" chip (bottom-right) */}
      <g transform="translate(386 488)" filter={`url(#${id}-shadow)`}>
        <rect width="178" height="50" rx="25" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 9)">
          <circle cx="16" cy="16" r="16" fill={`url(#${id}-accent)`} />
          {/* clock */}
          <circle cx="16" cy="16" r="9" fill="none" stroke="#FFFFFF" strokeWidth="2" />
          <line x1="16" y1="16" x2="16" y2="10" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
          <line x1="16" y1="16" x2="20" y2="18" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
        </g>
        <text x="56" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Avg response
        </text>
        <text x="56" y="38" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          Under 1 hour
        </text>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="58" cy="300" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="544" cy="300" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.8s" begin="0.8s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   10) VIRTUAL TERMINAL — browser-based payment terminal,
   invoice + send button, secure connection chip.
   ════════════════════════════════════════════════════════════ */
export function VirtualTerminalIllustration({ className = "" }) {
  const id = "vt1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Virtual terminal — browser-based payment form with secure checkout"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        accents={
          <g>
            <circle cx="86"  cy="106" r="44" fill="#0072FF" fillOpacity="0.07" />
            <circle cx="514" cy="514" r="58" fill="#0072FF" fillOpacity="0.08" />
            <rect x="498" y="76"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity="0.05" transform="rotate(12 530 108)" />
            <rect x="50"  y="466" width="56" height="56" rx="16" fill="#061A3A" fillOpacity="0.05" transform="rotate(-10 78 494)" />
          </g>
        }
      />

      {/* HERO — browser-style virtual terminal */}
      <g transform="translate(90 124)" filter={`url(#${id}-shadow)`}>
        <rect width="420" height="332" rx="22" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* browser chrome */}
        <rect width="420" height="44" rx="22" fill={`url(#${id}-navy)`} />
        <rect y="20" width="420" height="24" fill="#061A3A" />
        <circle cx="22" cy="22" r="5" fill="#0072FF" />
        <circle cx="42" cy="22" r="5" fill="#3A8EFF" />
        <circle cx="62" cy="22" r="5" fill="#FFFFFF" fillOpacity="0.55" />
        {/* URL pill with lock */}
        <g transform="translate(110 12)">
          <rect width="220" height="20" rx="10" fill="#FFFFFF" fillOpacity="0.12" />
          <g transform="translate(10 4)">
            <rect x="0" y="4" width="10" height="8" rx="1.5" fill="#3A8EFF" />
            <path d="M2 4 V2 a3 3 0 0 1 6 0 V4" stroke="#3A8EFF" strokeWidth="1.4" fill="none" />
          </g>
          <text x="28" y="14" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fontWeight="700" fill="#FFFFFF" fillOpacity="0.85">
            terminal.swipetronics.com
          </text>
        </g>

        {/* heading */}
        <g transform="translate(28 64)">
          <rect width="160" height="10" rx="5" fill="#061A3A" />
          <rect y="18" width="220" height="6" rx="3" fill="#061A3A" fillOpacity="0.4" />
        </g>

        {/* AMOUNT pill */}
        <g transform="translate(28 102)">
          <rect width="364" height="60" rx="14" fill="#EAF3FF" />
          <rect x="16" y="14" width="52" height="6" rx="3" fill="#061A3A" fillOpacity="0.55" />
          <text x="16" y="48" fontFamily="Inter, system-ui, sans-serif" fontSize="22" fontWeight="800" fill="#061A3A">
            $1,245.00
          </text>
          <g transform="translate(280 16)">
            <rect width="74" height="28" rx="14" fill={`url(#${id}-accent)`} />
            <text x="37" y="19" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#FFFFFF">USD</text>
          </g>
        </g>

        {/* CARD NUMBER row */}
        <g transform="translate(28 176)">
          <rect width="240" height="48" rx="12" fill="#F4F8FF" stroke="#061A3A" strokeOpacity="0.08" />
          <rect x="14" y="10" width="60" height="5" rx="2.5" fill="#061A3A" fillOpacity="0.55" />
          <g transform="translate(14 22)" fontFamily="ui-monospace, Menlo, monospace" fontSize="11" fontWeight="700" fill="#061A3A">
            <text y="12">4242 4242 4242 ●●●●</text>
          </g>
          <g transform="translate(196 14)">
            <rect width="32" height="20" rx="3" fill={`url(#${id}-accent)`} />
            <rect x="3" y="3" width="26" height="14" rx="2" fill="#FFFFFF" fillOpacity="0.6" />
          </g>

          <g transform="translate(252 0)">
            <rect width="112" height="48" rx="12" fill="#F4F8FF" stroke="#061A3A" strokeOpacity="0.08" />
            <rect x="14" y="10" width="40" height="5" rx="2.5" fill="#061A3A" fillOpacity="0.55" />
            <text x="14" y="34" fontFamily="ui-monospace, Menlo, monospace" fontSize="11" fontWeight="700" fill="#061A3A">CVV •••</text>
          </g>
        </g>

        {/* method chips */}
        <g transform="translate(28 238)">
          {["Card", "ACH", "Pay Link", "QR"].map((label, i) => (
            <g key={label} transform={`translate(${i * 92} 0)`}>
              <rect width="84" height="26" rx="13" fill={i === 0 ? `url(#${id}-accent)` : "#EAF3FF"} />
              <text x="42" y="17" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill={i === 0 ? "#FFFFFF" : "#0072FF"}>
                {label}
              </text>
            </g>
          ))}
        </g>

        {/* CHARGE button */}
        <g transform="translate(28 278)">
          <rect width="364" height="36" rx="10" fill={`url(#${id}-accent)`} />
          <text x="182" y="23" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#FFFFFF">CHARGE $1,245.00</text>
          {/* shimmer */}
          <rect x="0" y="0" width="60" height="36" rx="10" fill="#FFFFFF" fillOpacity="0.18">
            <animate attributeName="x" values="-60;364;-60" dur="3.4s" repeatCount="indefinite" />
          </rect>
        </g>
      </g>

      {/* FLOATING — INVOICE card (bottom-left, overlapping) */}
      <g transform="translate(48 412)" filter={`url(#${id}-shadow)`}>
        <rect width="200" height="118" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="200" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <g transform="translate(14 16)">
          <rect width="42" height="42" rx="8" fill="#EAF3FF" />
          <rect x="10" y="10" width="22" height="22" rx="2" fill="#0072FF" />
          <rect x="13" y="14" width="16" height="2" rx="1" fill="#FFFFFF" />
          <rect x="13" y="18" width="12" height="2" rx="1" fill="#FFFFFF" />
          <rect x="13" y="22" width="14" height="2" rx="1" fill="#FFFFFF" />
        </g>
        <text x="68" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Invoice #4821
        </text>
        <text x="68" y="52" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#061A3A">
          Sent by email
        </text>
        <g transform="translate(14 72)">
          <rect width="172" height="32" rx="8" fill={`url(#${id}-navy)`} />
          <text x="14" y="20" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#FFFFFF">PAY NOW</text>
          <g transform="translate(140 8)">
            <path d="M0 8 L14 8 M10 4 L14 8 L10 12" stroke="#FFFFFF" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </g>
        </g>
      </g>

      {/* FLOATING — SUCCESS chip (top-right) */}
      <g transform="translate(420 70)" filter={`url(#${id}-shadow)`}>
        <rect width="148" height="58" rx="29" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(10 9)">
          <circle cx="20" cy="20" r="20" fill={`url(#${id}-accent)`} />
          <path d="M11 20 L18 27 L29 16" stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="58" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Secure
        </text>
        <text x="58" y="44" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#061A3A">
          Tokenized
        </text>
      </g>

      {/* FLOATING — MOBILE PEEK (bottom-right) */}
      <g transform="translate(434 384)" filter={`url(#${id}-shadow)`}>
        <rect width="120" height="170" rx="20" fill={`url(#${id}-navy)`} />
        <rect x="8" y="22" width="104" height="138" rx="12" fill="#FFFFFF" />
        <rect x="44" y="10" width="32" height="5" rx="2.5" fill="#061A3A" />
        <g transform="translate(16 32)">
          <rect width="88" height="40" rx="10" fill={`url(#${id}-accent)`} />
          <text x="44" y="26" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#FFFFFF">$1,245</text>
        </g>
        <g transform="translate(16 82)">
          <rect width="88" height="22" rx="6" fill="#EAF3FF" />
          <rect x="8" y="8" width="40" height="6" rx="3" fill="#0072FF" />
        </g>
        <g transform="translate(16 110)">
          <rect width="88" height="22" rx="6" fill={`url(#${id}-accent)`} />
          <rect x="30" y="8" width="28" height="6" rx="3" fill="#FFFFFF" />
        </g>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="280" cy="500" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="540" cy="260" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.6s" begin="1s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   11) ONLINE PORTAL — merchant control center, transactions
   table, customer chips, activity feed.
   ════════════════════════════════════════════════════════════ */
export function OnlinePortalIllustration({ className = "" }) {
  const id = "op1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Online portal — merchant dashboard with transactions, customers, and activity"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        accents={
          <g>
            <circle cx="86"  cy="106" r="44" fill="#0072FF" fillOpacity="0.07" />
            <circle cx="514" cy="514" r="58" fill="#0072FF" fillOpacity="0.08" />
            <rect x="498" y="76"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity="0.05" transform="rotate(12 530 108)" />
            <rect x="50"  y="466" width="56" height="56" rx="16" fill="#061A3A" fillOpacity="0.05" transform="rotate(-10 78 494)" />
          </g>
        }
      />

      {/* HERO — full portal panel */}
      <g transform="translate(80 116)" filter={`url(#${id}-shadow)`}>
        <rect width="440" height="354" rx="22" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* header */}
        <rect width="440" height="50" rx="22" fill={`url(#${id}-navy)`} />
        <rect y="26" width="440" height="24" fill="#061A3A" />
        <g transform="translate(20 17)">
          <circle cx="8" cy="8" r="8" fill={`url(#${id}-accent)`} />
          <rect x="4" y="6" width="8" height="4" rx="1" fill="#FFFFFF" />
        </g>
        <text x="36" y="32" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#FFFFFF">Merchant Portal</text>
        <g transform="translate(370 18)">
          <circle cx="6" cy="6" r="6" fill="#3A8EFF">
            <animate attributeName="fill-opacity" values="1;0.45;1" dur="1.8s" repeatCount="indefinite" />
          </circle>
          <text x="16" y="10" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#FFFFFF" opacity="0.85">Live</text>
        </g>

        {/* KPI row */}
        <g transform="translate(20 66)">
          {[
            { label: "Volume", value: "$48,210", accent: true },
            { label: "Customers", value: "1,284" },
            { label: "Success", value: "99.4%" },
          ].map((k, i) => (
            <g key={k.label} transform={`translate(${i * 134} 0)`}>
              <rect width="126" height="56" rx="10" fill={k.accent ? `url(#${id}-accent)` : "#EAF3FF"} />
              <rect x="12" y="10" width="54" height="6" rx="3" fill={k.accent ? "#FFFFFF" : "#061A3A"} fillOpacity={k.accent ? 0.8 : 0.55} />
              <text x="12" y="40" fontFamily="Inter, system-ui, sans-serif" fontSize="15" fontWeight="800" fill={k.accent ? "#FFFFFF" : "#061A3A"}>
                {k.value}
              </text>
            </g>
          ))}
        </g>

        {/* transactions table */}
        <g transform="translate(20 138)">
          <rect width="280" height="194" rx="10" fill="#F4F8FF" stroke="#061A3A" strokeOpacity="0.06" />
          {/* header */}
          <rect x="0" y="0" width="280" height="26" rx="10" fill="#EAF3FF" />
          <rect x="12" y="10" width="60" height="6" rx="3" fill="#061A3A" fillOpacity="0.55" />
          <rect x="116" y="10" width="48" height="6" rx="3" fill="#061A3A" fillOpacity="0.55" />
          <rect x="190" y="10" width="40" height="6" rx="3" fill="#061A3A" fillOpacity="0.55" />
          <rect x="240" y="10" width="30" height="6" rx="3" fill="#061A3A" fillOpacity="0.55" />
          {/* rows */}
          {[
            { name: "Atlas Coffee Co", amt: "$84.20", status: "OK" },
            { name: "Riverside Auto",  amt: "$642.00", status: "OK" },
            { name: "Bloom Boutique",  amt: "$129.99", status: "OK" },
            { name: "Northpoint Salon", amt: "$48.50", status: "VOID" },
            { name: "Vista Bakery",    amt: "$215.40", status: "OK" },
          ].map((r, i) => (
            <g key={r.name} transform={`translate(0 ${36 + i * 30})`}>
              <rect x="6" y="0" width="268" height="24" rx="6" fill={i % 2 === 0 ? "#FFFFFF" : "#FFFFFF"} fillOpacity={i % 2 === 0 ? 0.6 : 0} />
              <circle cx="20" cy="12" r="6" fill={`url(#${id}-accent)`} />
              <text x="32" y="16" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A">{r.name}</text>
              <text x="170" y="16" fontFamily="ui-monospace, Menlo, monospace" fontSize="10" fontWeight="800" fill="#061A3A">{r.amt}</text>
              <g transform="translate(230 4)">
                <rect width="40" height="16" rx="8" fill={r.status === "VOID" ? "#EEF2F6" : `url(#${id}-accent)`} fillOpacity={r.status === "VOID" ? 1 : 1} />
                <text x="20" y="11" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="800" fill={r.status === "VOID" ? "#061A3A" : "#FFFFFF"}>
                  {r.status}
                </text>
              </g>
            </g>
          ))}
        </g>

        {/* RIGHT — customer & activity */}
        <g transform="translate(312 138)">
          <rect width="108" height="92" rx="10" fill="#EAF3FF" />
          <text x="12" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A" opacity="0.6">CUSTOMERS</text>
          {[0, 1, 2, 3].map((i) => (
            <g key={i} transform={`translate(12 ${32 + i * 14})`}>
              <circle cx="6" cy="6" r="6" fill={i % 2 ? "#3A8EFF" : "#0072FF"} fillOpacity={0.5 + i * 0.12} />
              <rect x="18" y="3" width="60" height="5" rx="2.5" fill="#061A3A" fillOpacity="0.55" />
            </g>
          ))}

          <g transform="translate(0 104)">
            <rect width="108" height="90" rx="10" fill={`url(#${id}-navy)`} />
            <text x="12" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#FFFFFF" opacity="0.6">ACTIVITY</text>
            {/* mini chart */}
            <polyline
              points="12,68 28,58 44,52 60,42 76,46 92,30"
              fill="none" stroke="#0072FF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
            />
            <circle cx="92" cy="30" r="3.5" fill="#FFFFFF" stroke="#0072FF" strokeWidth="2">
              <animate attributeName="r" values="3.5;6;3.5" dur="2.2s" repeatCount="indefinite" />
            </circle>
            <rect x="12" y="38" width="48" height="5" rx="2.5" fill="#FFFFFF" fillOpacity="0.55" />
            <text x="12" y="80" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#FFFFFF">+18.2%</text>
          </g>
        </g>
      </g>

      {/* FLOATING — REFUND ACTION chip (top-right) */}
      <g transform="translate(424 64)" filter={`url(#${id}-shadow)`}>
        <rect width="144" height="62" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <rect width="38" height="38" rx="10" fill={`url(#${id}-accent)`} />
          {/* refund arrow */}
          <path d="M14 22 a8 8 0 1 0 4 -10" stroke="#FFFFFF" strokeWidth="2.6" fill="none" strokeLinecap="round" />
          <path d="M10 20 L14 22 L16 18" stroke="#FFFFFF" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="60" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          1-click
        </text>
        <text x="60" y="46" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#061A3A">
          Refunds &amp; voids
        </text>
      </g>

      {/* FLOATING — DEPLOY badge (bottom-left) */}
      <g transform="translate(46 488)" filter={`url(#${id}-shadow)`}>
        <rect width="180" height="56" rx="28" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(10 8)">
          <circle cx="20" cy="20" r="20" fill={`url(#${id}-accent)`} />
          {/* rocket */}
          <g transform="translate(20 20)">
            <path d="M0 -10 C5 -6 7 -2 7 4 L0 10 L-7 4 C-7 -2 -5 -6 0 -10 Z" fill="#FFFFFF" />
            <circle r="2.5" fill={`url(#${id}-accent)`} />
            <path d="M-4 6 L-7 11 L-2 9 Z" fill="#FFFFFF" fillOpacity="0.65" />
            <path d="M4 6 L7 11 L2 9 Z" fill="#FFFFFF" fillOpacity="0.65" />
          </g>
        </g>
        <text x="56" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Fast boarding
        </text>
        <text x="56" y="42" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          Deploy in days
        </text>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="60" cy="300" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="540" cy="290" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.8s" begin="0.6s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   12) API / SDK — developer code editor with API call, response,
   200 OK badge.
   ════════════════════════════════════════════════════════════ */
export function ApiSdkIllustration({ className = "" }) {
  const id = "api1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="APIs and SDKs — developer code editor with clean payment integration"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        accents={
          <g>
            <circle cx="86"  cy="106" r="44" fill="#0072FF" fillOpacity="0.07" />
            <circle cx="514" cy="514" r="58" fill="#0072FF" fillOpacity="0.08" />
            <rect x="498" y="76"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity="0.05" transform="rotate(12 530 108)" />
            <rect x="50"  y="466" width="56" height="56" rx="16" fill="#061A3A" fillOpacity="0.05" transform="rotate(-10 78 494)" />
          </g>
        }
      />

      {/* HERO — IDE/code editor window */}
      <g transform="translate(80 120)" filter={`url(#${id}-shadow)`}>
        <rect width="440" height="320" rx="22" fill={`url(#${id}-navy)`} />
        {/* top bar */}
        <rect width="440" height="40" rx="22" fill="#0A2552" />
        <rect y="20" width="440" height="20" fill="#0A2552" />
        <circle cx="22" cy="20" r="5" fill="#0072FF" />
        <circle cx="42" cy="20" r="5" fill="#3A8EFF" />
        <circle cx="62" cy="20" r="5" fill="#FFFFFF" fillOpacity="0.6" />
        <g transform="translate(118 10)">
          <rect width="180" height="22" rx="6" fill="#061A3A" />
          <rect x="10" y="6" width="44" height="4" rx="2" fill="#0072FF" />
          <text x="60" y="15" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fontWeight="700" fill="#FFFFFF" fillOpacity="0.7">checkout.js</text>
        </g>
        {/* gutter */}
        <rect x="0" y="40" width="36" height="280" fill="#061A3A" />
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((n, i) => (
          <text
            key={n}
            x="22"
            y={68 + i * 22}
            textAnchor="end"
            fontFamily="ui-monospace, Menlo, monospace"
            fontSize="10"
            fontWeight="700"
            fill="#FFFFFF"
            fillOpacity="0.35"
          >
            {n}
          </text>
        ))}

        {/* code lines */}
        <g fontFamily="ui-monospace, Menlo, monospace" fontSize="11" fontWeight="600">
          {/* line 1 */}
          <g transform="translate(46 64)">
            <tspan x="0" y="0" fill="#3A8EFF">const</tspan>
            <text fill="#FFFFFF" x="36" y="0"> swipe = </text>
            <text fill="#3A8EFF" x="92" y="0">require</text>
            <text fill="#FFFFFF" x="144" y="0">(</text>
            <text fill="#0072FF" x="152" y="0">{`"swipetronics"`}</text>
            <text fill="#FFFFFF" x="244" y="0">);</text>
          </g>
          {/* line 2 - blank */}
          <text x="46" y="86" fill="#FFFFFF" fillOpacity="0.4">// init API client</text>
          {/* line 3 */}
          <g transform="translate(46 108)">
            <text fill="#3A8EFF" x="0" y="0">const</text>
            <text fill="#FFFFFF" x="36" y="0"> client = </text>
            <text fill="#FFFFFF" x="92" y="0">swipe.</text>
            <text fill="#0072FF" x="132" y="0">connect</text>
            <text fill="#FFFFFF" x="182" y="0">(KEY);</text>
          </g>
          {/* line 4 */}
          <g transform="translate(46 130)">
            <text fill="#FFFFFF" x="0" y="0">client.</text>
            <text fill="#0072FF" x="44" y="0">charges</text>
            <text fill="#FFFFFF" x="96" y="0">.</text>
            <text fill="#0072FF" x="100" y="0">create</text>
            <text fill="#FFFFFF" x="142" y="0">({"{"}</text>
          </g>
          {/* line 5 */}
          <g transform="translate(70 152)">
            <text fill="#FFFFFF" x="0" y="0">amount: </text>
            <text fill="#3A8EFF" x="48" y="0">2499</text>
            <text fill="#FFFFFF" x="78" y="0">,</text>
          </g>
          {/* line 6 */}
          <g transform="translate(70 174)">
            <text fill="#FFFFFF" x="0" y="0">currency: </text>
            <text fill="#0072FF" x="62" y="0">{`"usd"`}</text>
            <text fill="#FFFFFF" x="92" y="0">,</text>
          </g>
          {/* line 7 */}
          <g transform="translate(70 196)">
            <text fill="#FFFFFF" x="0" y="0">source: </text>
            <text fill="#0072FF" x="46" y="0">{`"tok_visa"`}</text>
            <text fill="#FFFFFF" x="112" y="0">,</text>
          </g>
          {/* line 8 */}
          <g transform="translate(70 218)">
            <text fill="#FFFFFF" x="0" y="0">capture: </text>
            <text fill="#3A8EFF" x="54" y="0">true</text>
          </g>
          {/* line 9 */}
          <g transform="translate(46 240)">
            <text fill="#FFFFFF" x="0" y="0">{"}"});</text>
          </g>
          {/* line 10 — blank */}
          {/* line 11 - cursor block */}
          <g transform="translate(46 284)">
            <rect width="8" height="14" fill="#0072FF">
              <animate attributeName="fill-opacity" values="1;0.15;1" dur="1.2s" repeatCount="indefinite" />
            </rect>
          </g>
        </g>

        {/* highlight bar on line 4 */}
        <rect x="36" y="116" width="404" height="22" fill="#0072FF" fillOpacity="0.10" />
      </g>

      {/* FLOATING — 200 OK response chip (top-right, overlapping) */}
      <g transform="translate(414 78)" filter={`url(#${id}-shadow)`}>
        <rect width="158" height="92" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="158" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <g transform="translate(14 18)">
          <rect width="42" height="22" rx="6" fill={`url(#${id}-accent)`} />
          <text x="21" y="15" textAnchor="middle" fontFamily="ui-monospace, Menlo, monospace" fontSize="10" fontWeight="800" fill="#FFFFFF">200 OK</text>
        </g>
        <g transform="translate(14 50)" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fontWeight="700" fill="#061A3A">
          <text y="0" fillOpacity="0.65">id: ch_8421</text>
          <text y="14">paid: true</text>
          <text y="28" fill="#0072FF">$24.99</text>
        </g>
      </g>

      {/* FLOATING — webhook / arrow connector */}
      <g transform="translate(60 408)" filter={`url(#${id}-shadow)`}>
        <rect width="220" height="116" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="220" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <text x="14" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A" opacity="0.6">WEBHOOK</text>
        {/* event row */}
        {[
          "charge.succeeded",
          "customer.created",
          "invoice.paid",
        ].map((evt, i) => (
          <g key={evt} transform={`translate(14 ${42 + i * 22})`}>
            <circle cx="6" cy="8" r="4" fill="#0072FF" />
            <text x="18" y="12" fontFamily="ui-monospace, Menlo, monospace" fontSize="10" fontWeight="700" fill="#061A3A">{evt}</text>
            <g transform="translate(170 4)">
              <rect width="28" height="12" rx="6" fill="#EAF3FF" />
              <text x="14" y="9" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="800" fill="#0072FF">200</text>
            </g>
          </g>
        ))}
      </g>

      {/* FLOATING — SDK pill (bottom-right) */}
      <g transform="translate(338 460)" filter={`url(#${id}-shadow)`}>
        <rect width="226" height="64" rx="32" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(14 12)">
          <rect width="40" height="40" rx="10" fill={`url(#${id}-accent)`} />
          {/* {} glyph */}
          <text x="20" y="28" textAnchor="middle" fontFamily="ui-monospace, Menlo, monospace" fontSize="20" fontWeight="800" fill="#FFFFFF">{`{ }`}</text>
        </g>
        <text x="64" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Drop-in SDK
        </text>
        <text x="64" y="48" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          Web · iOS · Android
        </text>
      </g>

      {/* traveling data dot from editor to response chip */}
      <g>
        <path id={`${id}-flow`} d="M510,200 C540,180 540,140 494,124" fill="none" stroke="#0072FF" strokeWidth="2" strokeDasharray="3 6" opacity="0.6" />
        <circle r="4" fill="#0072FF">
          <animateMotion dur="2.4s" repeatCount="indefinite">
            <mpath href={`#${id}-flow`} />
          </animateMotion>
        </circle>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="60" cy="320" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="540" cy="380" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.8s" begin="0.6s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   13) CHECKOUT INTEGRATION — secure browser checkout with
   mobile preview, padlock badge, framework chips.
   ════════════════════════════════════════════════════════════ */
export function CheckoutIntegrationIllustration({ className = "" }) {
  const id = "ci1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Secure online checkout with framework compatibility and mobile preview"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        accents={
          <g>
            <circle cx="86"  cy="106" r="44" fill="#0072FF" fillOpacity="0.07" />
            <circle cx="514" cy="514" r="58" fill="#0072FF" fillOpacity="0.08" />
            <rect x="498" y="76"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity="0.05" transform="rotate(12 530 108)" />
            <rect x="50"  y="466" width="56" height="56" rx="16" fill="#061A3A" fillOpacity="0.05" transform="rotate(-10 78 494)" />
          </g>
        }
      />

      {/* HERO — browser checkout */}
      <g transform="translate(76 132)" filter={`url(#${id}-shadow)`}>
        <rect width="392" height="312" rx="22" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* browser bar */}
        <rect width="392" height="42" rx="22" fill={`url(#${id}-navy)`} />
        <rect y="20" width="392" height="22" fill="#061A3A" />
        <circle cx="22" cy="22" r="5" fill="#0072FF" />
        <circle cx="42" cy="22" r="5" fill="#3A8EFF" />
        <circle cx="62" cy="22" r="5" fill="#FFFFFF" fillOpacity="0.55" />
        {/* secure URL pill */}
        <g transform="translate(108 12)">
          <rect width="220" height="20" rx="10" fill="#FFFFFF" fillOpacity="0.12" />
          <g transform="translate(10 5)">
            <rect x="0" y="4" width="9" height="7" rx="1.5" fill={`url(#${id}-accent)`} />
            <path d="M2 4 V2 a2.5 2.5 0 0 1 5 0 V4" stroke="#3A8EFF" strokeWidth="1.4" fill="none" />
          </g>
          <text x="26" y="14" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fontWeight="700" fill="#FFFFFF" fillOpacity="0.85">
            checkout.swipetronics.com
          </text>
          <g transform="translate(186 4)">
            <rect width="26" height="12" rx="6" fill={`url(#${id}-accent)`} />
            <text x="13" y="9" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="800" fill="#FFFFFF">SSL</text>
          </g>
        </g>

        {/* checkout content */}
        <g transform="translate(28 60)">
          {/* heading */}
          <rect width="140" height="10" rx="5" fill="#061A3A" />
          <rect y="18" width="200" height="6" rx="3" fill="#061A3A" fillOpacity="0.4" />

          {/* order summary card */}
          <g transform="translate(0 38)">
            <rect width="336" height="58" rx="12" fill="#EAF3FF" />
            <g transform="translate(14 14)">
              <rect width="30" height="30" rx="6" fill={`url(#${id}-accent)`} />
              <rect x="6" y="9" width="18" height="12" rx="2" fill="#FFFFFF" />
            </g>
            <rect x="56" y="16" width="120" height="6" rx="3" fill="#061A3A" />
            <rect x="56" y="28" width="80" height="5" rx="2.5" fill="#061A3A" fillOpacity="0.5" />
            <text x="300" y="34" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="16" fontWeight="800" fill="#061A3A">$84.20</text>
          </g>

          {/* card input */}
          <g transform="translate(0 110)">
            <rect width="336" height="44" rx="10" fill="#F4F8FF" stroke="#0072FF" strokeOpacity="0.35" />
            <rect x="14" y="8" width="80" height="5" rx="2.5" fill="#061A3A" fillOpacity="0.5" />
            <text x="14" y="32" fontFamily="ui-monospace, Menlo, monospace" fontSize="11" fontWeight="700" fill="#061A3A">●●●● ●●●● ●●●● 4242</text>
            <g transform="translate(296 12)">
              <rect width="28" height="20" rx="3" fill={`url(#${id}-accent)`} />
            </g>
            {/* focus pulse */}
            <rect width="336" height="44" rx="10" fill="none" stroke="#0072FF" strokeWidth="2" opacity="0.5">
              <animate attributeName="opacity" values="0.6;0.1;0.6" dur="2.4s" repeatCount="indefinite" />
            </rect>
          </g>

          {/* split inputs */}
          <g transform="translate(0 162)">
            <rect width="160" height="36" rx="10" fill="#F4F8FF" stroke="#061A3A" strokeOpacity="0.08" />
            <rect x="12" y="8" width="40" height="5" rx="2.5" fill="#061A3A" fillOpacity="0.5" />
            <text x="12" y="26" fontFamily="ui-monospace, Menlo, monospace" fontSize="10" fontWeight="700" fill="#061A3A">12 / 27</text>

            <g transform="translate(176 0)">
              <rect width="160" height="36" rx="10" fill="#F4F8FF" stroke="#061A3A" strokeOpacity="0.08" />
              <rect x="12" y="8" width="40" height="5" rx="2.5" fill="#061A3A" fillOpacity="0.5" />
              <text x="12" y="26" fontFamily="ui-monospace, Menlo, monospace" fontSize="10" fontWeight="700" fill="#061A3A">CVV •••</text>
            </g>
          </g>

          {/* PAY button */}
          <g transform="translate(0 210)">
            <rect width="336" height="38" rx="10" fill={`url(#${id}-accent)`} />
            <g transform="translate(120 11)">
              <rect width="14" height="11" rx="1.5" fill="#FFFFFF" />
              <path d="M3 11 V8 a4 4 0 0 1 8 0 V11" stroke="#FFFFFF" strokeWidth="1.8" fill="none" />
            </g>
            <text x="168" y="24" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#FFFFFF">PAY $84.20 SECURELY</text>
            {/* shimmer */}
            <rect width="60" height="38" rx="10" fill="#FFFFFF" fillOpacity="0.18">
              <animate attributeName="x" values="-60;336;-60" dur="3.4s" repeatCount="indefinite" />
            </rect>
          </g>
        </g>
      </g>

      {/* FLOATING — MOBILE preview */}
      <g transform="translate(404 168)" filter={`url(#${id}-shadow)`}>
        <rect width="132" height="252" rx="22" fill={`url(#${id}-navy)`} />
        <rect x="10" y="28" width="112" height="208" rx="14" fill="#FFFFFF" />
        <rect x="50" y="14" width="32" height="6" rx="3" fill="#061A3A" />
        {/* mini browser url */}
        <g transform="translate(20 40)">
          <rect width="92" height="16" rx="8" fill="#EAF3FF" />
          <rect x="6" y="6" width="6" height="4" rx="1" fill="#0072FF" />
          <rect x="18" y="6" width="48" height="4" rx="2" fill="#061A3A" fillOpacity="0.55" />
        </g>
        {/* summary */}
        <g transform="translate(20 66)">
          <rect width="92" height="44" rx="8" fill={`url(#${id}-accent)`} />
          <text x="46" y="28" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#FFFFFF">$84.20</text>
        </g>
        {/* fields */}
        <g transform="translate(20 120)">
          <rect width="92" height="20" rx="6" fill="#F4F8FF" stroke="#061A3A" strokeOpacity="0.08" />
          <rect x="8" y="8" width="60" height="4" rx="2" fill="#061A3A" fillOpacity="0.5" />
        </g>
        <g transform="translate(20 148)">
          <rect width="92" height="20" rx="6" fill="#F4F8FF" stroke="#061A3A" strokeOpacity="0.08" />
          <rect x="8" y="8" width="40" height="4" rx="2" fill="#061A3A" fillOpacity="0.5" />
        </g>
        {/* pay btn */}
        <g transform="translate(20 178)">
          <rect width="92" height="22" rx="6" fill={`url(#${id}-accent)`} />
          <rect x="30" y="9" width="32" height="4" rx="2" fill="#FFFFFF" />
        </g>
        {/* home indicator */}
        <rect x="56" y="222" width="20" height="3" rx="1.5" fill="#FFFFFF" fillOpacity="0.25" />
      </g>

      {/* FLOATING — Frameworks chip (top-right) */}
      <g transform="translate(380 78)" filter={`url(#${id}-shadow)`}>
        <rect width="186" height="56" rx="28" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <text x="16" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">
          Compatible with
        </text>
        <g transform="translate(16 28)">
          {["React", "Vue", "Next", "iOS"].map((f, i) => (
            <g key={f} transform={`translate(${i * 42} 0)`}>
              <rect width="36" height="20" rx="10" fill={i === 0 ? `url(#${id}-accent)` : "#EAF3FF"} />
              <text x="18" y="14" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill={i === 0 ? "#FFFFFF" : "#0072FF"}>{f}</text>
            </g>
          ))}
        </g>
      </g>

      {/* FLOATING — PCI / 3D-Secure chip (bottom-left) */}
      <g transform="translate(58 480)" filter={`url(#${id}-shadow)`}>
        <rect width="220" height="64" rx="32" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <circle cx="20" cy="20" r="20" fill={`url(#${id}-accent)`} />
          {/* shield */}
          <path
            d="M20 8 L30 12 L30 22 C30 28 26 32 20 34 C14 32 10 28 10 22 L10 12 Z"
            fill="#FFFFFF"
          />
          <path d="M14 22 L18 26 L26 18" stroke={`url(#${id}-accent)`} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="62" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Strong authentication
        </text>
        <text x="62" y="46" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          3D Secure · PCI DSS
        </text>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="60" cy="320" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="544" cy="80" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.8s" begin="0.6s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   14) SHOPPING CART — premium cart with floating platform tiles,
   subtotal card, "Add to cart" confirmation.
   ════════════════════════════════════════════════════════════ */
export function ShoppingCartIllustration({ className = "" }) {
  const id = "sc1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Shopping cart integrations — cart with connected store platforms and secure checkout"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        accents={
          <g>
            <circle cx="86"  cy="106" r="44" fill="#0072FF" fillOpacity="0.07" />
            <circle cx="514" cy="514" r="58" fill="#0072FF" fillOpacity="0.08" />
            <rect x="498" y="76"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity="0.05" transform="rotate(12 530 108)" />
            <rect x="50"  y="466" width="56" height="56" rx="16" fill="#061A3A" fillOpacity="0.05" transform="rotate(-10 78 494)" />
          </g>
        }
      />

      {/* CONNECTION PATHS from cart to platform tiles */}
      <g fill="none" strokeLinecap="round" opacity="0.6">
        <path id={`${id}-pTL`} d="M250,300 C160,260 110,180 130,116" stroke="#0072FF" strokeWidth="2" strokeDasharray="3 7" />
        <path id={`${id}-pTR`} d="M350,300 C440,260 490,180 470,116" stroke="#0072FF" strokeWidth="2" strokeDasharray="3 7" />
        <path id={`${id}-pBL`} d="M250,340 C160,380 110,460 130,520" stroke="#0072FF" strokeWidth="2" strokeDasharray="3 7" />
        <path id={`${id}-pBR`} d="M350,340 C440,380 490,460 470,520" stroke="#0072FF" strokeWidth="2" strokeDasharray="3 7" />
      </g>
      {[
        { p: `${id}-pTL`, d: "3.4s", b: "0s" },
        { p: `${id}-pTR`, d: "3.8s", b: "0.5s" },
        { p: `${id}-pBL`, d: "3.6s", b: "1s" },
        { p: `${id}-pBR`, d: "3.2s", b: "1.5s" },
      ].map((c) => (
        <circle key={c.p} r="4" fill="#0072FF">
          <animateMotion dur={c.d} repeatCount="indefinite" begin={c.b}>
            <mpath href={`#${c.p}`} />
          </animateMotion>
        </circle>
      ))}

      {/* HERO — cart card centered */}
      <g transform="translate(212 218)" filter={`url(#${id}-shadow)`}>
        <rect width="176" height="180" rx="22" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="176" height="6" rx="3" fill={`url(#${id}-accent)`} />
        {/* big cart icon */}
        <g transform="translate(46 28)" stroke={`url(#${id}-accent)`} strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 6 L14 6 L24 50 L78 50 L88 18 L20 18" />
          <circle cx="30" cy="68" r="6" fill={`url(#${id}-accent)`} stroke="none" />
          <circle cx="72" cy="68" r="6" fill={`url(#${id}-accent)`} stroke="none" />
        </g>
        {/* items inside cart */}
        <g transform="translate(34 52)">
          <rect width="14" height="14" rx="3" fill="#0072FF" />
          <rect x="20" width="14" height="14" rx="3" fill="#3A8EFF" />
          <rect x="40" width="14" height="14" rx="3" fill={`url(#${id}-navy)`} />
        </g>
        {/* subtotal */}
        <g transform="translate(20 124)">
          <rect width="136" height="42" rx="10" fill="#EAF3FF" />
          <rect x="12" y="10" width="44" height="6" rx="3" fill="#061A3A" fillOpacity="0.55" />
          <text x="12" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#061A3A">3 items · $84.20</text>
        </g>
      </g>

      {/* PLATFORM TILES (4 corners) */}
      {[
        { x: 50,  y: 56,  label: "Online Store", sub: "Cart" },
        { x: 408, y: 56,  label: "Custom Site",  sub: "API" },
        { x: 50,  y: 480, label: "Marketplace",  sub: "Plugin" },
        { x: 408, y: 480, label: "Mobile App",   sub: "SDK" },
      ].map((tile, i) => (
        <g key={tile.label} transform={`translate(${tile.x} ${tile.y})`} filter={`url(#${id}-shadow)`}>
          <rect width="142" height="68" rx="18" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
          <g transform="translate(12 14)">
            <rect width="40" height="40" rx="10" fill={i % 2 === 0 ? `url(#${id}-accent)` : "#EAF3FF"} />
            {/* glyph */}
            {i === 0 && (
              <g transform="translate(8 8)" fill={i % 2 === 0 ? "#FFFFFF" : "#0072FF"}>
                <rect x="2" y="6" width="20" height="14" rx="2" />
                <rect x="0" y="2" width="24" height="6" rx="2" />
              </g>
            )}
            {i === 1 && (
              <g transform="translate(10 12)" stroke={i % 2 === 0 ? "#FFFFFF" : "#0072FF"} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path d="M0 8 L4 4 L8 8" />
                <path d="M20 8 L16 12 L20 16" />
                <path d="M10 14 L14 2" />
              </g>
            )}
            {i === 2 && (
              <g transform="translate(8 8)" fill={i % 2 === 0 ? "#FFFFFF" : "#0072FF"}>
                <rect x="0" y="6" width="10" height="10" rx="2" />
                <rect x="14" y="6" width="10" height="10" rx="2" />
                <rect x="0" y="20" width="10" height="4" rx="1" />
                <rect x="14" y="20" width="10" height="4" rx="1" />
              </g>
            )}
            {i === 3 && (
              <g transform="translate(13 8)" fill={i % 2 === 0 ? "#FFFFFF" : "#0072FF"}>
                <rect width="14" height="24" rx="3" />
                <rect x="2" y="3" width="10" height="16" rx="1" fill={i % 2 === 0 ? `url(#${id}-accent)` : "#FFFFFF"} />
                <rect x="5" y="21" width="4" height="1.5" rx="0.75" fill={i % 2 === 0 ? "#FFFFFF" : "#0072FF"} />
              </g>
            )}
          </g>
          <text x="62" y="32" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#061A3A">{tile.label}</text>
          <text x="62" y="48" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.55">{tile.sub}</text>
        </g>
      ))}

      {/* FLOATING — Added to cart toast */}
      <g transform="translate(212 110)" filter={`url(#${id}-soft)`}>
        <rect width="176" height="36" rx="18" fill={`url(#${id}-navy)`} />
        <g transform="translate(10 8)">
          <circle cx="10" cy="10" r="10" fill={`url(#${id}-accent)`} />
          <path d="M5 10 L9 14 L15 6" stroke="#FFFFFF" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="38" y="23" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#FFFFFF">Added to cart</text>
        <text x="138" y="23" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill={`url(#${id}-accent)`}>$24.99</text>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="80" cy="300" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="520" cy="300" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.8s" begin="0.6s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   15) CASH DISCOUNT HERO — payment terminal + dual-price sign + savings chart
   Hero: terminal in center, floating cash/card pricing sign, receipt
   peeking, and a small savings trend card.
   ════════════════════════════════════════════════════════════ */
export function CashDiscountHeroIllustration({ className = "", transparent = false }) {
  const id = "cd1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Cash discount program — payment terminal with cash and card pricing sign and monthly savings chart"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        transparent={transparent}
        accents={
          <g>
            <circle cx="86"  cy="106" r="44" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <circle cx="510" cy="500" r="58" fill="#0072FF" fillOpacity={transparent ? "0.18" : "0.07"} />
            <rect x="498" y="72"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity={transparent ? "0.12" : "0.05"} transform="rotate(14 530 104)" />
            <rect x="52"  y="462" width="58" height="58" rx="16" fill="#FFFFFF" fillOpacity={transparent ? "0.06" : "0.0"} transform="rotate(-10 81 491)" />
          </g>
        }
      />

      {/* ── RECEIPT (behind terminal, peeking from left) ───── */}
      <g transform="translate(74 174) rotate(-8 80 156)" filter={`url(#${id}-shadow)`}>
        <path d="M0 0 H160 V300 L148 290 L132 300 L116 290 L100 300 L84 290 L68 300 L52 290 L36 300 L20 290 L0 300 Z" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="160" height="6" fill={`url(#${id}-accent)`} />
        {/* logo bar */}
        <g transform="translate(16 18)">
          <rect width="60" height="6" rx="3" fill="#061A3A" />
          <rect y="14" width="36" height="4" rx="2" fill="#061A3A" fillOpacity="0.4" />
        </g>
        {/* line items */}
        {[
          { y: 52, w1: 60, w2: 32 },
          { y: 70, w1: 70, w2: 28 },
          { y: 88, w1: 50, w2: 36 },
          { y: 106, w1: 64, w2: 30 },
        ].map((r, i) => (
          <g key={i} transform={`translate(16 ${r.y})`}>
            <rect width={r.w1} height="4" rx="2" fill="#061A3A" fillOpacity="0.6" />
            <rect x={128 - r.w2} y="0" width={r.w2} height="4" rx="2" fill="#061A3A" />
          </g>
        ))}
        <line x1="16" y1="130" x2="144" y2="130" stroke="#061A3A" strokeOpacity="0.12" strokeDasharray="2 3" />
        {/* Subtotal cash */}
        <g transform="translate(16 144)">
          <rect width="64" height="5" rx="2.5" fill="#061A3A" />
          <text x="128" y="6" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#061A3A">$14.00</text>
        </g>
        {/* Card price highlighted */}
        <g transform="translate(16 166)">
          <rect x="-4" y="-8" width="136" height="24" rx="5" fill="#EAF3FF" />
          <rect width="64" height="5" rx="2.5" fill="#0072FF" />
          <text x="128" y="6" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#0072FF">$14.50</text>
        </g>
        {/* footer */}
        <g transform="translate(16 200)">
          <rect width="100" height="4" rx="2" fill="#061A3A" fillOpacity="0.3" />
          <rect y="10" width="80" height="4" rx="2" fill="#061A3A" fillOpacity="0.3" />
        </g>
        {/* barcode */}
        <g transform="translate(16 230)">
          {[0,2,4,7,9,11,13,16,18,20,23,25,27,30,32,34,37,39,42,44,47,49,52,54,57,59,62,64,67,69,72,74,77,79,82,84,87,89,92,94,97,99,102,104,107,109,112,114,117,119,122,124].map((x, i) => (
            <rect key={i} x={x} y="0" width={i % 3 === 0 ? 2 : 1.2} height="28" fill="#061A3A" />
          ))}
        </g>
      </g>

      {/* ── HERO PAYMENT TERMINAL (center) ───────────────── */}
      <g transform="translate(216 168)" filter={`url(#${id}-shadow)`}>
        <rect width="186" height="296" rx="22" fill={`url(#${id}-card)`} stroke="#061A3A" strokeOpacity="0.08" />
        {/* screen */}
        <rect x="14" y="16" width="158" height="120" rx="10" fill={`url(#${id}-navy)`} />
        <rect x="22" y="24" width="62" height="6" rx="3" fill="#FFFFFF" fillOpacity="0.6" />
        <rect x="22" y="36" width="40" height="4" rx="2" fill="#FFFFFF" fillOpacity="0.35" />

        {/* dual price on screen */}
        <g transform="translate(22 52)">
          <rect width="68" height="62" rx="8" fill="#FFFFFF" fillOpacity="0.06" stroke="#FFFFFF" strokeOpacity="0.15" />
          <text x="34" y="22" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#7DB6FF">CASH</text>
          <text x="34" y="46" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="18" fontWeight="800" fill="#FFFFFF">$14.00</text>
          <rect x="14" y="52" width="40" height="3" rx="1.5" fill="#FFFFFF" fillOpacity="0.45" />
        </g>
        <g transform="translate(96 52)">
          <rect width="68" height="62" rx="8" fill={`url(#${id}-accent)`} />
          <text x="34" y="22" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">CARD</text>
          <text x="34" y="46" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="18" fontWeight="800" fill="#FFFFFF">$14.50</text>
          <rect x="14" y="52" width="40" height="3" rx="1.5" fill="#FFFFFF" fillOpacity="0.7" />
        </g>

        {/* card slot */}
        <rect x="14" y="146" width="158" height="6" rx="3" fill="#EEF2F6" />
        {/* keypad */}
        <g transform="translate(22 162)">
          {[0,1,2,3].map(r => (
            [0,1,2].map(c => (
              <g key={`k-${r}-${c}`} transform={`translate(${c * 52} ${r * 30})`}>
                <rect width="42" height="22" rx="6" fill={r === 3 && c === 1 ? `url(#${id}-accent)` : "#F4F8FF"} stroke="#061A3A" strokeOpacity="0.06" />
                {r < 3 && (
                  <text x="21" y="15" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">{r * 3 + c + 1}</text>
                )}
                {r === 3 && c === 0 && (
                  <text x="21" y="15" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">*</text>
                )}
                {r === 3 && c === 1 && (
                  <text x="21" y="15" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">OK</text>
                )}
                {r === 3 && c === 2 && (
                  <text x="21" y="15" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">#</text>
                )}
              </g>
            ))
          ))}
        </g>
        {/* pulse ring around OK key */}
        <circle cx="139" cy="262" r="22" fill="none" stroke="#0072FF" strokeOpacity="0.55" strokeWidth="2">
          <animate attributeName="r" values="22;30;22" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="stroke-opacity" values="0.55;0;0.55" dur="2.4s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* ── FLOATING DUAL-PRICING SIGN (top-right) ───────── */}
      <g transform="translate(414 70)" filter={`url(#${id}-shadow)`}>
        <rect width="152" height="106" rx="16" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="152" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <text x="14" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          At checkout
        </text>
        <text x="14" y="46" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#061A3A">
          Cash or Card?
        </text>
        <g transform="translate(14 56)">
          <rect width="58" height="38" rx="8" fill="#EAF3FF" />
          <text x="29" y="16" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="800" fill="#061A3A" opacity="0.6">CASH</text>
          <text x="29" y="32" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#061A3A">$14.00</text>
        </g>
        <g transform="translate(80 56)">
          <rect width="58" height="38" rx="8" fill={`url(#${id}-accent)`} />
          <text x="29" y="16" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="800" fill="#FFFFFF" opacity="0.85">CARD</text>
          <text x="29" y="32" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#FFFFFF">$14.50</text>
        </g>
      </g>

      {/* ── FLOATING SAVINGS CHART (bottom-right) ───────── */}
      <g transform="translate(414 380)" filter={`url(#${id}-shadow)`}>
        <rect width="160" height="124" rx="16" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="14" y="14" width="80" height="6" rx="3" fill="#061A3A" />
        <rect x="14" y="26" width="50" height="5" rx="2.5" fill="#0072FF" />
        <text x="14" y="58" fontFamily="Inter, system-ui, sans-serif" fontSize="18" fontWeight="800" fill="#061A3A">
          $420<tspan fontSize="10" fontWeight="700" fill="#0072FF">/mo</tspan>
        </text>
        {/* mini bars */}
        <g transform="translate(14 70)">
          {[14, 22, 18, 30, 26, 38, 34, 46].map((h, i) => (
            <rect key={i} x={i * 16} y={46 - h} width="10" height={h} rx="2" fill={i === 7 ? "#0072FF" : "#3A8EFF"} fillOpacity={i === 7 ? 1 : 0.45} />
          ))}
          {/* trendline */}
          <polyline
            points="5,32 21,24 37,28 53,16 69,20 85,8 101,12 117,0"
            fill="none" stroke="#0072FF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
          />
          <circle cx="117" cy="0" r="3.5" fill="#FFFFFF" stroke="#0072FF" strokeWidth="2">
            <animate attributeName="r" values="3.5;6;3.5" dur="2.2s" repeatCount="indefinite" />
          </circle>
        </g>
        {/* trend pill */}
        <g transform="translate(98 38)">
          <rect width="48" height="20" rx="10" fill="#EAF3FF" />
          <path d="M10 14 L14 8 L18 14 Z" fill="#0072FF" />
          <text x="32" y="14" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#0072FF">Saved</text>
        </g>
      </g>

      {/* ── FLOATING $ COIN STACK (bottom-left) ──────────── */}
      <g transform="translate(56 472)" filter={`url(#${id}-soft)`}>
        <rect width="184" height="60" rx="30" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <circle cx="18" cy="18" r="18" fill={`url(#${id}-accent)`} />
          <text x="18" y="24" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="20" fontWeight="800" fill="#FFFFFF">$</text>
        </g>
        <text x="58" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Lower effective rate
        </text>
        <text x="58" y="46" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          Keep more per sale
        </text>
      </g>

      {/* sparkles */}
      <g>
        <circle cx="540" cy="260" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="60" cy="300" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.8s" begin="0.6s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════
   16) DUAL PRICING — Tablet/POS prompt showing customer choice
   Hero: large tablet showing cash/card prompt UI; orbit:
   countertop sign and a customer-choice tap chip.
   ════════════════════════════════════════════════════════════ */
export function DualPricingIllustration({ className = "" }) {
  const id = "dp1";
  return (
    <svg
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Customer-facing dual pricing prompt on a POS tablet"
    >
      <SharedDefs id={id} />
      <PanelBackdrop
        id={id}
        accents={
          <g>
            <circle cx="86"  cy="110" r="42" fill="#0072FF" fillOpacity="0.07" />
            <circle cx="514" cy="510" r="58" fill="#0072FF" fillOpacity="0.07" />
            <rect x="498" y="78"  width="64" height="64" rx="18" fill="#0072FF" fillOpacity="0.05" transform="rotate(14 532 110)" />
            <rect x="50"  y="468" width="56" height="56" rx="16" fill="#061A3A" fillOpacity="0.05" transform="rotate(-10 78 496)" />
          </g>
        }
      />

      {/* HERO — tablet showing dual pricing UI */}
      <g transform="translate(124 122)" filter={`url(#${id}-shadow)`}>
        <rect width="352" height="356" rx="26" fill={`url(#${id}-navy)`} />
        <rect x="14" y="36" width="324" height="284" rx="14" fill="#FFFFFF" />
        <rect x="158" y="14" width="36" height="6" rx="3" fill="#FFFFFF" fillOpacity="0.4" />

        {/* header */}
        <g transform="translate(34 56)">
          <text x="0" y="14" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="700" fill="#061A3A" opacity="0.55">
            ORDER #4821
          </text>
          <text x="0" y="40" fontFamily="Inter, system-ui, sans-serif" fontSize="20" fontWeight="800" fill="#061A3A">
            Choose payment
          </text>
          <text x="0" y="60" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="600" fill="#061A3A" opacity="0.55">
            Same products. You decide how to pay.
          </text>
        </g>

        {/* CASH option */}
        <g transform="translate(34 132)">
          <rect width="138" height="148" rx="14" fill="#EAF3FF" stroke="#0072FF" strokeOpacity="0.15" />
          <g transform="translate(50 22)">
            <rect width="38" height="26" rx="4" fill={`url(#${id}-navy)`} />
            <rect x="3" y="3" width="32" height="20" rx="2" fill="#EAF3FF" />
            <circle cx="19" cy="13" r="6" fill="#0072FF" fillOpacity="0.4" />
            <text x="19" y="17" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#FFFFFF">$</text>
          </g>
          <text x="69" y="80" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A" opacity="0.6">PAY WITH CASH</text>
          <text x="69" y="108" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="22" fontWeight="800" fill="#061A3A">$14.00</text>
          <rect x="34" y="122" width="70" height="14" rx="7" fill="#FFFFFF" />
          <text x="69" y="132" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#0072FF">SELECT</text>
        </g>

        {/* CARD option (highlighted) */}
        <g transform="translate(180 132)">
          <rect width="138" height="148" rx="14" fill={`url(#${id}-accent)`} />
          {/* glow ring */}
          <rect width="138" height="148" rx="14" fill="none" stroke="#FFFFFF" strokeOpacity="0.55" strokeWidth="2">
            <animate attributeName="stroke-opacity" values="0.6;0.15;0.6" dur="2.2s" repeatCount="indefinite" />
          </rect>
          <g transform="translate(46 22)">
            <rect width="44" height="28" rx="4" fill="#FFFFFF" />
            <rect width="44" height="10" rx="4" fill="#061A3A" fillOpacity="0.85" />
            <rect x="4" y="18" width="14" height="6" rx="1" fill={`url(#${id}-accent)`} />
            <g transform="translate(28 16)" stroke={`url(#${id}-accent)`} strokeWidth="1.8" fill="none" strokeLinecap="round">
              <path d="M0 8 Q4 4 8 8" />
              <path d="M-3 11 Q4 2 11 11" />
            </g>
          </g>
          <text x="69" y="80" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#FFFFFF" opacity="0.85">PAY WITH CARD</text>
          <text x="69" y="108" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="22" fontWeight="800" fill="#FFFFFF">$14.50</text>
          <rect x="34" y="122" width="70" height="14" rx="7" fill="#FFFFFF" />
          <text x="69" y="132" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#0072FF">SELECT</text>
        </g>

        {/* footer note */}
        <g transform="translate(34 296)">
          <circle cx="6" cy="6" r="4" fill="#0072FF" />
          <text x="18" y="10" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" opacity="0.6">
            A small service fee applies to card payments.
          </text>
        </g>

        {/* home indicator */}
        <rect x="162" y="334" width="28" height="4" rx="2" fill="#FFFFFF" fillOpacity="0.5" />
      </g>

      {/* FLOATING — counter sign (top-right) */}
      <g transform="translate(412 62)" filter={`url(#${id}-shadow)`}>
        <rect width="148" height="120" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="148" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <text x="74" y="30" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A" opacity="0.55">
          POSTED PRICING
        </text>
        <g transform="translate(16 42)">
          <rect width="116" height="22" rx="6" fill="#EAF3FF" />
          <text x="14" y="15" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#061A3A">CASH</text>
          <text x="102" y="15" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#061A3A">$14.00</text>
        </g>
        <g transform="translate(16 70)">
          <rect width="116" height="22" rx="6" fill={`url(#${id}-accent)`} />
          <text x="14" y="15" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#FFFFFF">CARD</text>
          <text x="102" y="15" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#FFFFFF">$14.50</text>
        </g>
        <text x="74" y="108" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#061A3A" opacity="0.5">
          Customer chooses at checkout
        </text>
      </g>

      {/* FLOATING — choice chip (bottom-left, overlapping) */}
      <g transform="translate(46 472)" filter={`url(#${id}-shadow)`}>
        <rect width="208" height="62" rx="31" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <g transform="translate(12 12)">
          <circle cx="19" cy="19" r="19" fill={`url(#${id}-accent)`} />
          <path d="M11 19 L17 25 L28 14" stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="58" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
          Transparent choice
        </text>
        <text x="58" y="46" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
          No checkout surprises
        </text>
      </g>

      {/* tap pulse near card option */}
      <g transform="translate(394 256)">
        <circle r="14" fill="none" stroke="#0072FF" strokeOpacity="0.55" strokeWidth="2">
          <animate attributeName="r" values="14;26;14" dur="2.2s" repeatCount="indefinite" />
          <animate attributeName="stroke-opacity" values="0.55;0;0.55" dur="2.2s" repeatCount="indefinite" />
        </circle>
        <circle r="8" fill={`url(#${id}-accent)`} />
        <circle r="3.5" fill="#FFFFFF" />
      </g>

      {/* sparkles */}
      <g>
        <circle cx="60" cy="290" r="3" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="544" cy="320" r="2.5" fill="#0072FF">
          <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="3.8s" begin="0.6s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}

const Illustrations = {
  GrowthIllustration,
  EcosystemIllustration,
  AnywhereIllustration,
  MissionIllustration,
  SecurityIllustration,
  ProcessIllustration,
  ResourcesIllustration,
  PartnershipIllustration,
  ContactIllustration,
  VirtualTerminalIllustration,
  OnlinePortalIllustration,
  ApiSdkIllustration,
  CheckoutIntegrationIllustration,
  ShoppingCartIllustration,
  CashDiscountHeroIllustration,
  DualPricingIllustration,
};
export default Illustrations;
